import Phaser from 'phaser';
import { useGameStore } from '../store/gameStore';
import { PoolSimulation, type LetterBallData } from '@physics/pool/PoolSimulation';
import { PocketDetector } from '@physics/pool/PocketDetector';
import { BallLauncher } from '@physics/bowling/BallLauncher';
import { drawRandomLetters, returnLetters } from '@game/models/LetterPool';
import { createGame, getCurrentPlayer, updatePlayer, switchPlayer, incrementConsecutivePasses, isStalemate } from '@game/models/Game';
import type { RackLetter } from '@game/models/Game';
import { addLettersToHand } from '@game/models/Player';
import type { Letter } from '@game/models/Letter';
import { updatePhase } from '@game/models/Game';
import { getPlayerColor } from '@game/utils/PlayerColors';
import { TABLE_LENGTH, TABLE_WIDTH, BALL_RADIUS, LETTER_BALL_RADIUS, POCKET_RADIUS, POCKET_POSITIONS, POCKET_SIZE_MULTIPLIERS } from '@physics/config/PhysicsConfig';
import { isStuckOnRail } from '@physics/stuckBallDetection';
import { getBallPosition, RACK_CENTER } from '@physics/config/BallRackLayout';
import { COLORS, TYPOGRAPHY } from '../ui/theme';
import { MultiplayerManager } from '../multiplayer/managers/MultiplayerManager';
import { GameActionType } from '../multiplayer/types/NetworkTypes';
import type { GameStateUpdatePayload, TurnChangePayload } from '../multiplayer/types/NetworkTypes';

/**
 * PoolScene: Main pool table gameplay scene
 * Shoot cue ball to sink letter balls into pockets
 */
export class PoolScene extends Phaser.Scene {
  private simulation!: PoolSimulation;
  private pocketDetector!: PocketDetector;
  private ballLauncher!: BallLauncher;
  
  // Game objects
  private letterBallData: LetterBallData[] = [];
  private collectedLetters: Letter[] = []; // All letters collected this turn (pocketed successfully)
  private gutteredLetters: Letter[] = []; // Letters lost to gutter/out-of-bounds this turn (will be returned to pool)
  private gutteredThisTurn = 0; // Count of guttered letters this turn (for messaging)
  private lettersThisShot = 0; // Letters collected on THIS shot only
  private lastBallClearedByGutterThisShot = false; // True only when a gutter event removes the final unresolved rack ball this shot
  private cueBallHitAnyBall = false; // Track if cue ball hit any letter ball this shot
  
  // Sprite maps
  private cueBallSprite?: Phaser.GameObjects.Arc;
  private letterBallSprites: Map<string, Phaser.GameObjects.Arc> = new Map();
  private prevBallPositions: Map<string, { x: number; y: number }> = new Map();
  private letterTexts: Map<string, Phaser.GameObjects.Text> = new Map();
  private pocketGraphics?: Phaser.GameObjects.Graphics;
  
  // Input state
  private isPositioning = false;
  private isDragging = false;
  private dragStartX = 0;
  private dragStartY = 0;
  private isReady = false;
  private cueBallStartX = 0;
  
  // UI
  private aimLineGraphics!: Phaser.GameObjects.Graphics;
  private powerBarGraphics!: Phaser.GameObjects.Graphics;
  private playerHandText?: Phaser.GameObjects.Text;
  private rackInfoText?: Phaser.GameObjects.Text;
  private turnIndicatorText?: Phaser.GameObjects.Text;
  private playerStatusText?: Phaser.GameObjects.Text;
  private turnEndMessageText?: Phaser.GameObjects.Text;
  private turnEndContinueText?: Phaser.GameObjects.Text;
  private pendingBoardPromptTimer?: Phaser.Time.TimerEvent;
  private pendingBoardPointerHandler?: () => void;
  private scoreboardCards: Map<number, Phaser.GameObjects.Graphics> = new Map();
  private scoreboardTexts: Map<number, Phaser.GameObjects.Text> = new Map();
  
  // Multiplayer
  private multiplayerManager?: MultiplayerManager;
  private isMyTurn = false;
  private currentPlayerNumber = 1;
  private gameActionHandler?: (actionType: string, data: any) => void;
  // gameStateSync removed — unused
  private turnInfoText?: Phaser.GameObjects.Text;
  
  // Rendering constants
  private readonly SCALE = 100; // pixels per meter (pool table needs tighter view)
  private readonly TABLE_OFFSET_X = 640; // Center horizontally
  private readonly TABLE_OFFSET_Y = 620; // Position so table top appears below UI text (~Y=120)
  
  // Game state
  private isShooting = false;
  private turnActive = true;
  private showTargetLine = false; // Settings: show extended aim line
  private pocketRadius = POCKET_RADIUS; // Current pocket size (from settings)
  private isFirstShot = true; // Can reposition cue ball only after scratch or on fresh rack
  private scratchDetected = false; // Track if scratch occurred, but wait for balls to stop
  private ballsInGutters: Set<string> = new Set(); // Track which balls are in gutters
  private readonly MAX_HAND_SIZE = 10; // Maximum letters in hand before turn ends
  
  // Stuck ball detection
  private ballPositionHistory: Map<string, { x: number; y: number; time: number }[]> = new Map();
  private readonly STUCK_CHECK_INTERVAL = 1000; // Check every 1 second
  private readonly STUCK_TIME_THRESHOLD = 3000; // 3 seconds without movement
  private lastStuckCheck = 0;
  
  // Rack generation timeout (safety for network delays)
  private rackGenerationTimer: NodeJS.Timeout | null = null;
  private readonly RACK_GENERATION_TIMEOUT = 30000; // 30 seconds before non-Player1 tries to generate
  private freshRackGenerated = false; // Set when a new full rack is generated; triggers cue ball reset
  private multiplayerSubscriptions: Array<{ unsubscribe: () => void }> = [];

  constructor() {
    super({ key: 'PoolScene' });
  }

  create(): void {
    console.log('PoolScene: create()');
    
    // Clear any pending rack generation timeout from previous instance
    if (this.rackGenerationTimer) {
      clearTimeout(this.rackGenerationTimer);
      this.rackGenerationTimer = null;
    }
    
    // Reset all state variables (scene may be reused when returning from board)
    this.collectedLetters = [];
    this.gutteredLetters = [];
    this.gutteredThisTurn = 0;
    this.lettersThisShot = 0;
    this.lastBallClearedByGutterThisShot = false;
    this.ballsInGutters.clear();
    this.ballPositionHistory.clear();
    this.lastStuckCheck = 0;
    this.scratchDetected = false;
    this.letterBallSprites.clear();
    this.letterTexts.clear();
    this.scoreboardCards.clear();
    this.scoreboardTexts.clear();
    this.isPositioning = false;
    this.isDragging = false;
    this.dragStartX = 0;
    this.dragStartY = 0;
    this.cueBallStartX = 0;
    this.isShooting = false;
    this.turnActive = true;
    this.isReady = false;
    this.playerHandText = undefined;
    this.rackInfoText = undefined;
    this.turnIndicatorText = undefined;
    this.playerStatusText = undefined;
    this.turnInfoText = undefined;
    this.clearPendingTurnEndUI();

    // Initialize multiplayer context early so startup state does not fall back
    // to stale local persisted data from a previous game/session.
    this.setupMultiplayer();
    this.syncStoreToMultiplayerSessionIfNeeded();
    
    // Update game phase to pool
    const store = useGameStore.getState();
    
    if (store.game) {
      console.log('PoolScene.create - player scores at start:', store.game.players.map(p => `${p.name}: ${p.score}`).join(', '));

      // Read settings (with defaults for backward compatibility)
      const settings = store.game.settings || {
        pocketSize: 'medium' as const,
        showTargetLine: false,
        timerMode: 'off' as const,
      };
      const pocketSizeMultiplier = POCKET_SIZE_MULTIPLIERS[settings.pocketSize];
      this.pocketRadius = POCKET_RADIUS * pocketSizeMultiplier;
      this.showTargetLine = settings.showTargetLine;
      console.log(`🎱 Pocket size: ${settings.pocketSize} (${this.pocketRadius}m), Target line: ${this.showTargetLine}`);
      
      const updatedGame = updatePhase(store.game, 'pool');
      store.setGame(updatedGame);
      
      // Check if player already has a full hand - if so, skip pool and go straight to board
      const currentPlayer = getCurrentPlayer(updatedGame);
      if (currentPlayer.hand.length >= this.MAX_HAND_SIZE) {
        console.log(`🎱 Player already has ${currentPlayer.hand.length} letters (max ${this.MAX_HAND_SIZE}) - skipping pool scene`);
        
        // Show brief message
        const messageText = this.add.text(640, 360, `Hand is full (${currentPlayer.hand.length} letters)!\nGoing to word board...`, {
          fontSize: '24px',
          color: '#FFFF00',
          backgroundColor: '#000000',
          padding: { x: 16, y: 8 },
          align: 'center',
          fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        }).setOrigin(0.5).setDepth(100);
        
        // Go to board after short delay — but only if it's still our turn.
        // In multiplayer the TURN_HANDOFF may arrive during this window and flip
        // isMyTurn to false (e.g. after a timer-triggered pass), in which case we
        // should stay in PoolScene as a spectator rather than opening BoardScene.
        this.time.delayedCall(1500, () => {
          messageText.destroy();
          if (this.isMultiplayerSessionActive() && !this.isMyTurn) return;
          this.scene.start('BoardScene');
        });
        return;
      }
      
      // Only allow repositioning if cue ball was scratched (null position) or fresh rack with no saved position.
      // In multiplayer, use authoritative session game state instead of local persisted state.
      const sessionCueBallPosition = this.getSessionCueBallPosition();
      const cueBallPositionForTurn = this.isMultiplayerSessionActive()
        ? (sessionCueBallPosition ?? null)
        : updatedGame.cueBallPosition;
      this.isFirstShot = cueBallPositionForTurn === null;
      if (this.isFirstShot) {
        console.log('🎱 Cue ball can be repositioned (scratch or fresh game)');
      } else {
        console.log('🎱 Cue ball locked at saved position - no repositioning allowed');
      }
    } else {
      this.isFirstShot = true;
    }
    
    // Initialize physics
    this.simulation = new PoolSimulation();
    
    // Set up collision callback to track cue ball hits
    this.simulation.setCollisionCallback((ball1Id: string, ball2Id: string) => {
      // Check if one is cue ball and the other is a letter ball
      const isCueBallHit = (ball1Id === 'cue-ball' || ball2Id === 'cue-ball') &&
                          (ball1Id !== 'cue-ball' || ball2Id !== 'cue-ball');
      if (isCueBallHit && this.isShooting) {
        this.cueBallHitAnyBall = true;
      }
    });
    
    this.pocketDetector = new PocketDetector(this.pocketRadius);
    this.ballLauncher = new BallLauncher();
    
    // Setup pocket detection callbacks
    this.pocketDetector.onPocket((event) => {
      this.onBallPocketed(event.ballId, event.pocketName);
    });
    
    // Create graphics
    this.aimLineGraphics = this.add.graphics();
    this.aimLineGraphics.setDepth(20);
    this.powerBarGraphics = this.add.graphics();
    this.powerBarGraphics.setDepth(20);
    
    // Setup game
    const gameState = useGameStore.getState().game!;
    console.log('🎱 PoolScene.create: About to setupLetterBalls, game.currentRackLetters has', gameState.currentRackLetters.length, 'balls');
    this.setupLetterBalls();
    
    // Render scene
    this.renderTable();
    this.renderPockets();
    this.renderLetterBalls();
    this.renderCueBall();
    
    // Setup input
    this.setupInputHandlers();
    
    // Render UI
    this.renderUI();
    
    // Delay input readiness to ensure multiplayer state is set
    this.time.delayedCall(500, () => {
      this.isReady = true;
    });
  }

  private isMultiplayerSessionActive(): boolean {
    const sessionState = this.multiplayerManager?.getSessionState();
    return !!(this.multiplayerManager?.isInSession() && sessionState?.isMultiplayer);
  }

  private getSessionCueBallPosition(): { x: number; y: number } | null | undefined {
    if (!this.isMultiplayerSessionActive()) {
      return undefined;
    }

    const sessionState = this.multiplayerManager?.getSessionState();
    const sessionGameState = sessionState?.gameState;
    if (!sessionGameState || !Object.prototype.hasOwnProperty.call(sessionGameState, 'cueBallPosition')) {
      return null;
    }

    return sessionGameState.cueBallPosition ?? null;
  }

  private syncCurrentPlayerToStore(playerNumber: number): void {
    const store = useGameStore.getState();
    const game = store.game;
    if (!game) {
      return;
    }

    if (game.currentPlayerId === playerNumber) {
      return;
    }

    store.setGame({
      ...game,
      currentPlayerId: playerNumber,
      updatedAt: new Date(),
    });
  }

  private buildPlayerHandsSnapshotFromStore(): Record<number, Letter[]> {
    const game = useGameStore.getState().game;
    if (!game) {
      return {};
    }

    const snapshot: Record<number, Letter[]> = {};
    for (const player of game.players) {
      snapshot[player.id] = [...player.hand];
    }

    return snapshot;
  }

  private buildPlayerScoresSnapshotFromStore(): Record<number, number> {
    const game = useGameStore.getState().game;
    if (!game) {
      return {};
    }

    const snapshot: Record<number, number> = {};
    for (const player of game.players) {
      snapshot[player.id] = player.score;
    }

    return snapshot;
  }

  private applyPlayerHandsSnapshotToStore(rawPlayerHands: unknown): void {
    if (!rawPlayerHands || typeof rawPlayerHands !== 'object') {
      return;
    }

    const playerHands = rawPlayerHands as Record<string, unknown>;
    const store = useGameStore.getState();
    const game = store.game;
    if (!game) {
      return;
    }

    let didChange = false;
    const nextPlayers = game.players.map((player) => {
      const rawHand = playerHands[String(player.id)];
      if (!Array.isArray(rawHand)) {
        return player;
      }

      didChange = true;
      return {
        ...player,
        hand: rawHand as Letter[],
      };
    });

    if (!didChange) {
      return;
    }

    store.setGame({
      ...game,
      players: nextPlayers,
      updatedAt: new Date(),
    });
    this.updatePlayerHandUI();
  }

  private applyPlayerScoresSnapshotToStore(rawPlayerScores: unknown): void {
    if (!rawPlayerScores || typeof rawPlayerScores !== 'object') {
      return;
    }

    const playerScores = rawPlayerScores as Record<string, unknown>;
    const store = useGameStore.getState();
    const game = store.game;
    if (!game) {
      return;
    }

    let didChange = false;
    const nextPlayers = game.players.map((player) => {
      const rawScore = playerScores[String(player.id)];
      if (typeof rawScore !== 'number' || !Number.isFinite(rawScore)) {
        return player;
      }

      const nextScore = Math.max(0, Math.floor(rawScore));
      if (nextScore === player.score) {
        return player;
      }

      didChange = true;
      return {
        ...player,
        score: nextScore,
      };
    });

    if (!didChange) {
      return;
    }

    store.setGame({
      ...game,
      players: nextPlayers,
      updatedAt: new Date(),
    });
    this.updateScoreboardScores();
  }

  private updateScoreboardScores(): void {
    const game = useGameStore.getState().game;
    if (!game) {
      return;
    }

    const sessionPlayers = this.multiplayerManager?.getSessionState()?.players || [];
    game.players.forEach((player, index) => {
      const scoreText = this.scoreboardTexts.get(index + 1);
      if (!scoreText) {
        return;
      }

      const sessionPlayer = sessionPlayers.find(sp => sp.playerNumber === index + 1);
      const displayName = sessionPlayer?.displayName || player.name;
      scoreText.setText(`${displayName}  ${player.score}`);
    });
  }

  private getLocalPlayer(game: import('../game/models/Game').Game): import('../game/models/Player').Player {
    const sessionState = this.multiplayerManager?.getSessionState();
    if (sessionState?.isMultiplayer && typeof sessionState.playerNumber === 'number') {
      const localByIndex = game.players[sessionState.playerNumber - 1];
      if (localByIndex) return localByIndex;
    }
    return getCurrentPlayer(game);
  }

  private updatePlayerHandUI(): void {
    const handText = this.playerHandText;
    if (!handText || !handText.active || !handText.scene) {
      return;
    }

    const store = useGameStore.getState();
    const game = store.game;
    if (!game) {
      return;
    }

    const localPlayer = this.getLocalPlayer(game);
    const handDisplay = localPlayer.hand.map(l => l.character.toUpperCase()).join(' ') || '(empty)';
    try {
      handText.setText(`Hand (${localPlayer.hand.length}/${this.MAX_HAND_SIZE}): ${handDisplay}`);
      this.updateRackInfoUI(localPlayer.hand.length);
    } catch (error) {
      console.warn('[PoolScene] Failed to update hand text (likely stale UI object during scene transition)', error);
      this.playerHandText = undefined;
      this.rackInfoText = undefined;
    }
  }

  private updateRackInfoUI(handSizeOverride?: number): void {
    const rackInfoText = this.rackInfoText;
    if (!rackInfoText || !rackInfoText.active || !rackInfoText.scene) {
      return;
    }

    const game = useGameStore.getState().game;
    if (!game) {
      return;
    }

    const localPlayer = this.getLocalPlayer(game);
    const handSize = handSizeOverride ?? localPlayer.hand.length;
    const rackSize = this.letterBallData.length;
    const totalAvailable = handSize + rackSize;
    const rackInfo = handSize > 0
      ? `Letters: ${totalAvailable} total (${handSize} in hand, ${rackSize} on table)`
      : `Letters: ${rackSize} on table`;

    rackInfoText.setText(rackInfo);
  }

  private syncStoreToMultiplayerSessionIfNeeded(): void {
    if (!this.isMultiplayerSessionActive()) {
      return;
    }

    const sessionState = this.multiplayerManager?.getSessionState();
    const sessionId = sessionState?.sessionId;
    if (!sessionId) {
      return;
    }

    const store = useGameStore.getState();
    const localGame = store.game;

    // Already scoped to this active session - no reset needed.
    if (localGame?.id === sessionId) {
      return;
    }

    const defaults = {
      pocketSize: 'medium' as const,
      showTargetLine: false,
      timerMode: 'off' as const,
      numPlayers: 2,
    };
    const preservedSettings = localGame?.settings || defaults;

    const sessionPlayers = [...(sessionState?.players || [])]
      .filter(player => typeof player.playerNumber === 'number')
      .sort((a, b) => (a.playerNumber as number) - (b.playerNumber as number));

    const playerNames =
      sessionPlayers.length >= 2
        ? sessionPlayers.map(player => player.displayName || `Player ${player.playerNumber}`)
        : ['Player 1', 'Player 2'];

    const sessionGameState = sessionState?.gameState || {};
    const seededGame = createGame(playerNames, sessionGameState.boardSeed);
    seededGame.id = sessionId;
    seededGame.phase = 'pool';
    seededGame.settings = {
      ...preservedSettings,
      numPlayers: playerNames.length,
    };
    const sessionCurrentPlayer = sessionGameState.currentPlayer ?? sessionGameState.currentPlayerNumber;
    if (
      typeof sessionCurrentPlayer === 'number' &&
      sessionCurrentPlayer >= 1 &&
      sessionCurrentPlayer <= seededGame.players.length
    ) {
      seededGame.currentPlayerId = sessionCurrentPlayer;
    }

    if (typeof sessionGameState.turn === 'number' && Number.isFinite(sessionGameState.turn)) {
      seededGame.turnNumber = Math.max(0, Math.floor(sessionGameState.turn) - 1);
    }

    if (Array.isArray(sessionGameState.currentRackLetters)) {
      seededGame.currentRackLetters = sessionGameState.currentRackLetters;
    }

    if (sessionGameState.playerHands && typeof sessionGameState.playerHands === 'object') {
      seededGame.players = seededGame.players.map((player) => {
        const serializedHand = (sessionGameState.playerHands as Record<string, unknown>)[String(player.id)];
        if (!Array.isArray(serializedHand)) {
          return player;
        }
        return {
          ...player,
          hand: serializedHand as Letter[],
        };
      });
    }

    if (sessionGameState.playerScores && typeof sessionGameState.playerScores === 'object') {
      seededGame.players = seededGame.players.map((player) => {
        const rawScore = (sessionGameState.playerScores as Record<string, unknown>)[String(player.id)];
        if (typeof rawScore !== 'number' || !Number.isFinite(rawScore)) {
          return player;
        }

        return {
          ...player,
          score: Math.max(0, Math.floor(rawScore)),
        };
      });
    }

    if (Object.prototype.hasOwnProperty.call(sessionGameState, 'cueBallPosition')) {
      seededGame.cueBallPosition = sessionGameState.cueBallPosition ?? null;
    }

    if (sessionGameState.letterPool && typeof sessionGameState.letterPool === 'object') {
      seededGame.letterPool = sessionGameState.letterPool;
    }

    if (sessionGameState.consecutivePassesPerPlayer && typeof sessionGameState.consecutivePassesPerPlayer === 'object') {
      seededGame.consecutivePassesPerPlayer = sessionGameState.consecutivePassesPerPlayer;
    }

    store.setGame(seededGame);
    console.log('[PoolScene] Seeded local store from multiplayer session state', {
      sessionId,
      currentPlayerId: seededGame.currentPlayerId,
      turnNumber: seededGame.turnNumber,
      rackSize: seededGame.currentRackLetters.length,
      cueBallPosition: seededGame.cueBallPosition,
    });
  }

  private setupLetterBalls(): void {
    // Check if we're in multiplayer mode and use session state
    if (this.multiplayerManager?.isInSession()) {
      this.setupMultiplayerLetterBalls();
      return;
    }
    
    // Single player mode - use local game state
    const store = useGameStore.getState();
    const game = store.game;
    
    if (!game) {
      console.error('No game state found');
      return;
    }
    
    console.log('setupLetterBalls - player scores:', game.players.map(p => `${p.name}: ${p.score}`).join(', '));
    console.log('setupLetterBalls - letter pool status:', {
      totalRemaining: game.letterPool.totalRemaining,
      isExhausted: game.letterPool.isExhausted,
    });
    
    let updatedPool = game.letterPool;
    const rackLetters = game.currentRackLetters;
    
    // Check if there's an existing rack to continue
    if (rackLetters.length > 0) {
      // Continue with existing rack at saved positions
      console.log('setupLetterBalls - continuing existing rack with', rackLetters.length, 'letters:', 
                  rackLetters.map(rl => `${rl.letter.character}@(${rl.x.toFixed(2)},${rl.y.toFixed(2)})`).join(', '));
      
      // Create letter ball data with IDs
      this.letterBallData = rackLetters.map((rackLetter, index) => ({
        id: `ball-${index + 1}`,
        letter: rackLetter.letter,
        ballNumber: index + 1,
      }));
      
      // Build position map
      const positionMap = new Map<string, { x: number; y: number }>();
      this.letterBallData.forEach((data, index) => {
        positionMap.set(data.id, { x: rackLetters[index].x, y: rackLetters[index].y });
      });
      
      // Create physics bodies at saved positions
      this.simulation.createLetterBallsAtPositions(this.letterBallData, positionMap);
    } else {
      // Calculate rack size based on current hand (variable rack size for carry-over)
      const currentPlayer = getCurrentPlayer(game);
      const handSize = currentPlayer.hand.length;
      const rackSize = Math.max(1, 15 - handSize); // Between 1-15 letters (15 for empty hand)
      
      console.log(`🎱 Setting up rack: Player has ${handSize} letters, drawing ${rackSize} new letters (max hand: ${this.MAX_HAND_SIZE})`);
      
      // Draw calculated number of new letters for a fresh rack
      const drawResult = drawRandomLetters(game.letterPool, rackSize, 4);
      const letters = drawResult.letters;
      updatedPool = drawResult.updatedPool;
      
      console.log('setupLetterBalls - drew', letters.length, 'letters for NEW RACK');
      console.log('setupLetterBalls - updated pool:', {
        totalRemaining: updatedPool.totalRemaining,
        isExhausted: updatedPool.isExhausted,
      });
      
      // Create letter ball data
      this.letterBallData = letters.map((letter, index) => ({
        id: `ball-${index + 1}`,
        letter,
        ballNumber: index + 1,
      }));
      
      // Create physics bodies in triangle formation
      this.simulation.createLetterBalls(this.letterBallData);

      // Fresh rack = fresh break: allow cue ball repositioning from the starting line.
      this.isFirstShot = true;
      this.isPositioning = true;
    }

    // Update game with updated pool (only changes on fresh rack).
    // Null cueBallPosition on fresh rack so renderCueBall places ball at the default spot.
    const updatedGame = {
      ...game,
      letterPool: updatedPool,
      ...(updatedPool !== game.letterPool ? { cueBallPosition: null } : {}),
    };
    store.setGame(updatedGame);
    console.log('setupLetterBalls - after setup, player scores:', updatedGame.players.map(p => `${p.name}: ${p.score}`).join(', '));
    console.log('setupLetterBalls - created', this.letterBallData.length, 'letter ball data entries');
  }

  private renderTable(): void {
    const graphics = this.add.graphics();
    
    const tableWidthPx = TABLE_WIDTH * this.SCALE;
    const tableLengthPx = TABLE_LENGTH * this.SCALE;
    const x = this.TABLE_OFFSET_X - tableWidthPx / 2;
    const y = this.TABLE_OFFSET_Y - tableLengthPx; // Start from bottom, go up
    
    // Table felt (green)
    graphics.fillStyle(0x0e6b3d, 1);
    graphics.fillRect(x, y, tableWidthPx, tableLengthPx);
    
    // Rails (wood)
    const railWidth = 15;
    
    // Calculate gutter zone (lower half of table)
    const sidePocketY = POCKET_POSITIONS.SIDE_LEFT.y;
    const gutterEndY = sidePocketY / 2; // ~1.9m from bottom in physics
    const gutterEndPx = this.physicsToScreen(0, gutterEndY).y;
    const gutterHeight = y + tableLengthPx - gutterEndPx;
    
    // GUTTERS (darker, show as gaps in lower section)
    graphics.fillStyle(0x1a1a1a, 1); // Dark gray/black for gutters
    // Left gutter
    graphics.fillRect(x - railWidth, gutterEndPx, railWidth, gutterHeight);
    // Right gutter
    graphics.fillRect(x + tableWidthPx, gutterEndPx, railWidth, gutterHeight);
    
    // CUSHIONS/BUMPERS (wood with felt padding, from gutter end to back)
    graphics.fillStyle(0x8B4513, 1); // Wood
    const cushionHeight = tableLengthPx - gutterHeight;
    // Left cushion
    graphics.fillRect(x - railWidth, y, railWidth, cushionHeight);
    // Right cushion
    graphics.fillRect(x + tableWidthPx, y, railWidth, cushionHeight);
    
    // Add felt padding on cushions (lighter green strip)
    graphics.fillStyle(0x15a047, 1);
    const feltPadding = 4;
    graphics.fillRect(x - feltPadding, y, feltPadding, cushionHeight);
    graphics.fillRect(x + tableWidthPx, y, feltPadding, cushionHeight);
    
    // FRONT rail (solid - shooting area at bottom)
    const frontY = y + tableLengthPx;
    graphics.fillStyle(0x8B4513, 1);
    graphics.fillRect(x - railWidth, frontY, tableWidthPx + railWidth * 2, railWidth);
    
    // BACK rail segments (at top, between pockets)
    graphics.fillStyle(0x8B4513, 1);
    const pocketRadiusPx = this.pocketRadius * this.SCALE;
    const backY = y; // Top of table
    
    // Left segment: from left edge to left of center pocket
    const leftSegmentStart = x - railWidth;
    const leftSegmentEnd = this.TABLE_OFFSET_X - pocketRadiusPx;
    graphics.fillRect(leftSegmentStart, backY - railWidth, leftSegmentEnd - leftSegmentStart, railWidth);
    
    // Right segment: from right of center pocket to right edge
    const rightSegmentStart = this.TABLE_OFFSET_X + pocketRadiusPx;
    const rightSegmentEnd = x + tableWidthPx + railWidth;
    graphics.fillRect(rightSegmentStart, backY - railWidth, rightSegmentEnd - rightSegmentStart, railWidth);
    
    // Add felt padding on back cushions
    graphics.fillStyle(0x15a047, 1);
    graphics.fillRect(leftSegmentStart, backY - feltPadding, leftSegmentEnd - leftSegmentStart, feltPadding);
    graphics.fillRect(rightSegmentStart, backY - feltPadding, rightSegmentEnd - rightSegmentStart, feltPadding);
    
    // Cue line area (lighter area at front/bottom)
    graphics.fillStyle(0x12864f, 0.3);
    graphics.fillRect(x, y + tableLengthPx * 0.8, tableWidthPx, tableLengthPx * 0.2);
    
    // Head spot (where cue ball starts - at bottom)
    graphics.fillStyle(0xffffff, 0.5);
    graphics.fillCircle(
      this.TABLE_OFFSET_X,
      y + tableLengthPx * 0.9,
      3
    );
  }

  private renderPockets(): void {
    this.pocketGraphics = this.add.graphics();
    const pockets = this.pocketDetector.getPocketPositions();
    
    for (const pocket of pockets) {
      const screenPos = this.physicsToScreen(pocket.x, pocket.y);
      const pocketRadiusPx = this.pocketRadius * this.SCALE;
      
      // Draw pocket as black circle
      this.pocketGraphics.fillStyle(0x000000, 1);
      this.pocketGraphics.fillCircle(screenPos.x, screenPos.y, pocketRadiusPx);
      
      // Pocket highlight
      this.pocketGraphics.fillStyle(0x333333, 0.8);
      this.pocketGraphics.fillCircle(screenPos.x, screenPos.y, pocketRadiusPx * 0.6);
    }
    
    this.pocketGraphics.setDepth(1);
  }

  private renderLetterBalls(): void {
    // Clear old sprites before rendering new ones
    for (const sprite of this.letterBallSprites.values()) {
      sprite.destroy();
    }
    for (const text of this.letterTexts.values()) {
      text.destroy();
    }
    this.letterBallSprites.clear();
    this.letterTexts.clear();

    // Render each ball with fresh sprites
    for (const data of this.letterBallData) {
      const ballBody = this.simulation.getLetterBall(data.id);
      if (!ballBody) continue;
      
      const screenPos = this.physicsToScreen(ballBody.x, ballBody.y);
      const radiusPx = LETTER_BALL_RADIUS * this.SCALE;
      
      // Color based on point value and letter (for consistency)
      const points = data.letter.points;
      const { color, textColor } = this.getColorForPoints(points, data.letter.character);
      
      const ballSprite = this.add.circle(screenPos.x, screenPos.y, radiusPx, color);
      ballSprite.setStrokeStyle(1, 0x000000); // Thinner outline
      ballSprite.setDepth(5);
      
      // Add letter text
      const text = this.add.text(screenPos.x, screenPos.y, data.letter.character.toUpperCase(), {
        fontSize: '14px',
        fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        color: textColor,
        fontStyle: 'bold',
      }).setOrigin(0.5);
      text.setDepth(6);
      
      this.letterBallSprites.set(data.id, ballSprite);
      this.letterTexts.set(data.id, text);
    }
  }
  
  private getColorForPoints(points: number, character: string): { color: number; textColor: string } {
    // Low point letters: paler/pastel colors with black text
    // High point letters: vibrant/darker colors with white text
    // Use character code to ensure same letter always gets same color
    
    if (points === 1) {
      // Pale, pastel colors for 1-point letters
      const paleColors = [0xFFF9C4, 0xB2EBF2, 0xC8E6C9, 0xFFE0B2, 0xE1BEE7]; // Pale Yellow, Pale Cyan, Pale Green, Pale Orange, Pale Purple
      const index = character.toUpperCase().charCodeAt(0) % paleColors.length;
      return { 
        color: paleColors[index], 
        textColor: '#000000' 
      };
    } else if (points === 2) {
      // Light blue/green variety
      const colors2pt = [0x90CAF9, 0xA5D6A7, 0xCE93D8, 0xFFCC80]; // Light blue, light green, light purple, light orange
      const index = character.toUpperCase().charCodeAt(0) % colors2pt.length;
      return { color: colors2pt[index], textColor: '#000000' };
    } else if (points === 3) {
      // Medium colors with variety
      const colors3pt = [0x64B5F6, 0x81C784, 0xBA68C8, 0xFFB74D]; // Medium blue, green, purple, orange
      const index = character.toUpperCase().charCodeAt(0) % colors3pt.length;
      return { color: colors3pt[index], textColor: '#000000' };
    } else if (points === 4) {
      // Richer colors
      const colors4pt = [0x42A5F5, 0x66BB6A, 0xAB47BC, 0xFFA726]; // Rich blue, green, purple, orange
      const index = character.toUpperCase().charCodeAt(0) % colors4pt.length;
      return { color: colors4pt[index], textColor: '#FFFFFF' };
    } else if (points === 5) {
      // Deeper colors with variety
      const colors5pt = [0x2196F3, 0x4CAF50, 0x9C27B0, 0xFF9800]; // Deep blue, green, purple, orange
      const index = character.toUpperCase().charCodeAt(0) % colors5pt.length;
      return { color: colors5pt[index], textColor: '#FFFFFF' };
    } else if (points === 8) {
      // Vibrant darker colors
      const colors8pt = [0x1976D2, 0x388E3C, 0x7B1FA2, 0xF57C00]; // Vibrant blue, green, purple, orange
      const index = character.toUpperCase().charCodeAt(0) % colors8pt.length;
      return { color: colors8pt[index], textColor: '#FFFFFF' };
    } else if (points === 10) {
      // Very vibrant/dark colors
      const colors10pt = [0xD32F2F, 0x1565C0, 0x6A1B9A, 0xE65100]; // Red, dark blue, dark purple, dark orange
      const index = character.toUpperCase().charCodeAt(0) % colors10pt.length;
      return { 
        color: colors10pt[index], 
        textColor: '#FFFFFF' 
      };
    }
    
    // Default fallback
    return { color: 0xCCCCCC, textColor: '#000000' };
  }

  private renderCueBall(): void {
    const store = useGameStore.getState();
    const game = store.game;
    const sessionCueBallPosition = this.getSessionCueBallPosition();
    
    // Use saved cue ball position if available, otherwise use default starting position
    let startPos = this.getDefaultCueBallPosition(); // Default: 10% up from bottom

    if (this.isMultiplayerSessionActive()) {
      if (sessionCueBallPosition && typeof sessionCueBallPosition.x === 'number' && typeof sessionCueBallPosition.y === 'number') {
        startPos = { x: sessionCueBallPosition.x, y: sessionCueBallPosition.y };
        console.log('🎱 Restoring cue ball from multiplayer session state:', startPos);
      } else {
        console.log('🎱 Creating cue ball at default starting position');
      }
    } else if (game?.cueBallPosition) {
      startPos = { x: game.cueBallPosition.x, y: game.cueBallPosition.y };
      console.log('🎱 Restoring cue ball at saved position:', startPos);
    } else {
      console.log('🎱 Creating cue ball at default starting position');
    }
    
    this.simulation.createCueBall(startPos);
    
    const screenPos = this.physicsToScreen(startPos.x, startPos.y);
    const radiusPx = BALL_RADIUS * this.SCALE;
    
    // White cue ball
    this.cueBallSprite = this.add.circle(screenPos.x, screenPos.y, radiusPx, 0xFFFFFF);
    this.cueBallSprite.setStrokeStyle(2, 0x000000);
    this.cueBallSprite.setDepth(10);
  }

  private renderUI(): void {
    const store = useGameStore.getState();
    const game = store.game;
    
    if (!game) return;
    
    const localPlayer = this.getLocalPlayer(game);
    const handDisplay = localPlayer.hand.map(l => l.character.toUpperCase()).join(' ') || '(empty)';
    const playerColor = getPlayerColor(localPlayer.id);
    
    // Color box for local player
    this.add.rectangle(20, 30, 20, 20, playerColor.primary)
      .setOrigin(0, 0.5)
      .setDepth(100);
    
    this.playerHandText = this.add.text(45, 20, `Hand (${localPlayer.hand.length}/${this.MAX_HAND_SIZE}): ${handDisplay}`, {
      fontSize: '18px',
      color: '#ffff00',
      backgroundColor: '#000000',
      padding: { x: 8, y: 4 },
      fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    });
    this.playerHandText.setDepth(100);
    
    const collected = this.collectedLetters.map(l => l.character.toUpperCase()).join(' ') || 'none';
    this.turnInfoText = this.add.text(20, 50, `This turn: ${collected}`, {
      fontSize: '16px',
      color: '#00ff00',
      backgroundColor: '#000000',
      padding: { x: 6, y: 3 },
      fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    });
    this.turnInfoText.setDepth(100);
    
    // Rack size info (show letters available to local player)
    this.rackInfoText = this.add.text(20, 80, '', {
      fontSize: '14px',
      color: '#cccccc',
      backgroundColor: '#000000',
      padding: { x: 6, y: 3 },
      fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    }).setDepth(100);
    this.updateRackInfoUI(localPlayer.hand.length);
    
    const sessionState = this.multiplayerManager?.getSessionState();
    const multiplayerTurnNumber = sessionState?.gameState?.turn;
    const displayedTurn =
      this.isMultiplayerSessionActive() && typeof multiplayerTurnNumber === 'number' && Number.isFinite(multiplayerTurnNumber)
        ? Math.max(1, Math.floor(multiplayerTurnNumber))
        : game.turnNumber + 1;

    // Turn number
    this.add.text(20, 110, `Turn ${displayedTurn}`, {
      fontSize: '14px',
      color: '#aaaaaa',
      backgroundColor: '#000000',
      padding: { x: 6, y: 3 },
      fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    }).setDepth(100);
    
    // Score display with player colors and actual names
    let scoreY = 20;
    
    // Get multiplayer player names if available
    const multiplayerPlayers = sessionState?.players || [];
    const activePlayerId = this.isMultiplayerSessionActive() ? this.currentPlayerNumber : localPlayer.id;
    
    game.players.forEach((player, index) => {
      const pColor = getPlayerColor(player.id);
      const isCurrent = player.id === activePlayerId;
      
      // Use actual player names from multiplayer session if available
      const playerNumber = index + 1;
      const sessionPlayer = multiplayerPlayers.find(sp => sp.playerNumber === playerNumber);
      const displayName = sessionPlayer?.displayName || player.name;
      
      // Player score card with modern styling
      const cardWidth = 200;
      const cardHeight = 32;
      const cardX = 1260 - cardWidth;
      
      const scoreCard = this.add.graphics();
      scoreCard.fillStyle(isCurrent ? COLORS.primary.main : COLORS.background.tertiary, isCurrent ? 0.3 : 0.6);
      scoreCard.fillRoundedRect(cardX, scoreY - 2, cardWidth, cardHeight, 6);
      if (isCurrent) {
        scoreCard.lineStyle(2, COLORS.primary.main, 1);
        scoreCard.strokeRoundedRect(cardX, scoreY - 2, cardWidth, cardHeight, 6);
      }
      scoreCard.setDepth(99);
      
      // Store scoreboard card for later updates
      this.scoreboardCards.set(playerNumber, scoreCard);
      
      // Color indicator circle
      const colorCircle = this.add.graphics();
      colorCircle.fillStyle(pColor.primary, 1);
      colorCircle.fillCircle(cardX + 12, scoreY + 14, 6);
      colorCircle.setDepth(100);
      
      // Player name and score
      const playerScoreText = this.add.text(cardX + 26, scoreY + 14, `${displayName}  ${player.score}`, {
        fontSize: TYPOGRAPHY.fontSize.sm,
        color: isCurrent ? COLORS.text.accent : COLORS.text.primary,
        fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      });
      playerScoreText.setOrigin(0, 0.5).setDepth(100);
      
      // Store scoreboard text for later updates
      this.scoreboardTexts.set(playerNumber, playerScoreText);
      
      scoreY += 40;
    });
    
    // Instructions with modern card
    const instructionsBg = this.add.graphics();
    instructionsBg.fillStyle(COLORS.background.secondary, 0.9);
    instructionsBg.fillRoundedRect(340, 10, 600, 32, 8);
    instructionsBg.setDepth(99);
    
    this.add.text(640, 26, 'Position cue ball, then drag to shoot • Sink letters to collect', {
      fontSize: TYPOGRAPHY.fontSize.sm,
      color: COLORS.text.primary,
      fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    }).setOrigin(0.5).setDepth(100);
    
    // "Play Word" button with modern styling
    const buttonWidth = 180;
    const buttonHeight = 44;
    const buttonX = 1100;
    const buttonY = 680;
    
    const playWordShadow = this.add.graphics();
    playWordShadow.fillStyle(0x000000, 0.3);
    playWordShadow.fillRoundedRect(buttonX - buttonWidth / 2 + 2, buttonY - buttonHeight / 2 + 2, buttonWidth, buttonHeight, 8);
    playWordShadow.setDepth(98);
    
    const playWordBg = this.add.graphics();
    playWordBg.fillStyle(COLORS.success.main, 1);
    playWordBg.fillRoundedRect(buttonX - buttonWidth / 2, buttonY - buttonHeight / 2, buttonWidth, buttonHeight, 8);
    playWordBg.setDepth(99);
    playWordBg.setInteractive(
      new Phaser.Geom.Rectangle(buttonX - buttonWidth / 2, buttonY - buttonHeight / 2, buttonWidth, buttonHeight),
      Phaser.Geom.Rectangle.Contains
    );
    
    this.add.text(buttonX, buttonY, 'Play Word →', {
      fontSize: TYPOGRAPHY.fontSize.lg,
      color: '#ffffff',
      fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    }).setOrigin(0.5).setDepth(100);
    
    playWordBg.on('pointerover', () => {
      playWordBg.clear();
      playWordBg.fillStyle(COLORS.success.light, 1);
      playWordBg.fillRoundedRect(buttonX - buttonWidth / 2, buttonY - buttonHeight / 2, buttonWidth, buttonHeight, 8);
      this.input.setDefaultCursor('pointer');
    });
    
    playWordBg.on('pointerout', () => {
      playWordBg.clear();
      playWordBg.fillStyle(COLORS.success.main, 1);
      playWordBg.fillRoundedRect(buttonX - buttonWidth / 2, buttonY - buttonHeight / 2, buttonWidth, buttonHeight, 8);
      this.input.setDefaultCursor('default');
    });
    
    playWordBg.on('pointerdown', () => {
      if (!this.isShooting && this.turnActive) {
        // Check if player has any letters in hand (including from previous turns)
        const store = useGameStore.getState();
        const game = store.game!;
        const currentPlayer = getCurrentPlayer(game);
        
        // Can go to board if they have letters in hand OR just collected letters
        if (currentPlayer.hand.length > 0 || this.collectedLetters.length > 0) {
          this.goToBoard();
        }
      }
    });
  }
  
  private setupInputHandlers(): void {
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (!this.isReady || !this.cueBallSprite  || this.isShooting || !this.turnActive || !this.isMyTurn) return;
      
      const cueBall = this.simulation.getCueBall();
      if (!cueBall) return;
      
      const distance = Phaser.Math.Distance.Between(
        pointer.x, pointer.y,
        this.cueBallSprite.x, this.cueBallSprite.y
      );
      
      if (distance < 50) {
        // Only allow repositioning on first shot, otherwise go straight to aiming
        if (this.isFirstShot) {
          this.isPositioning = true;
          this.dragStartX = pointer.x;
          this.dragStartY = pointer.y;
          this.cueBallStartX = this.cueBallSprite.x;
        } else {
          // After first shot, immediately start dragging to aim
          this.isDragging = true;
          this.dragStartX = this.cueBallSprite.x;
          this.dragStartY = this.cueBallSprite.y;
        }
      }
    });

    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (this.isPositioning && this.cueBallSprite && !this.isDragging) {
        const horizontalDelta = pointer.x - this.dragStartX;
        const verticalDelta = Math.abs(pointer.y - this.dragStartY);
        
        if (Math.abs(horizontalDelta) > 5 || verticalDelta < 20) {
          // Position cue ball left/right within bounds
          const tableWidthPx = TABLE_WIDTH * this.SCALE;
          const minX = this.TABLE_OFFSET_X - tableWidthPx / 2 + 20;
          const maxX = this.TABLE_OFFSET_X + tableWidthPx / 2 - 20;
          
          const newX = Phaser.Math.Clamp(
            this.cueBallStartX + horizontalDelta,
            minX,
            maxX
          );
          
          this.cueBallSprite.setX(newX);
          
          // Update physics body
          const physicsPos = this.screenToPhysics(newX, this.cueBallSprite.y);
          this.simulation.setCueBallPosition(physicsPos);
        } else if (verticalDelta > 20) {
          this.isPositioning = false;
          this.isDragging = true;
          this.dragStartX = this.cueBallSprite.x;
          this.dragStartY = this.cueBallSprite.y;
        }
      }
      
      if (this.isDragging && this.cueBallSprite && this.aimLineGraphics && this.powerBarGraphics) {
        this.aimLineGraphics.clear();
        this.powerBarGraphics.clear();
        
        // Draw pullback line
        this.aimLineGraphics.lineStyle(2, 0xffaa00, 0.5);
        this.aimLineGraphics.lineBetween(
          this.cueBallSprite.x,
          this.cueBallSprite.y,
          pointer.x,
          pointer.y
        );
        
        // Calculate aim direction (opposite of pull)
        const dx = this.cueBallSprite.x - pointer.x;
        const dy = this.cueBallSprite.y - pointer.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance > 0) {
          const dirX = dx / distance;
          const dirY = dy / distance;
          
          // Standard aim line length
          const aimLength = distance;
          const oppositeX = this.cueBallSprite.x + dirX * aimLength;
          const oppositeY = this.cueBallSprite.y + dirY * aimLength;
          
          this.aimLineGraphics.lineStyle(4, 0x00ff00, 0.8);
          this.aimLineGraphics.lineBetween(
            this.cueBallSprite.x,
            this.cueBallSprite.y,
            oppositeX,
            oppositeY
          );
          
          // Extended target line if setting is enabled
          if (this.showTargetLine) {
            const extendedLength = 800; // Extend much further
            const targetX = this.cueBallSprite.x + dirX * extendedLength;
            const targetY = this.cueBallSprite.y + dirY * extendedLength;
            
            // Draw dashed line by drawing segments
            this.aimLineGraphics.lineStyle(2, 0x00ff00, 0.3);
            const dashLength = 10;
            const gapLength = 5;
            const segmentLength = dashLength + gapLength;
            const lineLength = Math.sqrt((targetX - oppositeX) ** 2 + (targetY - oppositeY) ** 2);
            const numSegments = Math.floor(lineLength / segmentLength);
            
            for (let i = 0; i < numSegments; i++) {
              const t1 = (i * segmentLength) / lineLength;
              const t2 = ((i * segmentLength) + dashLength) / lineLength;
              const x1 = oppositeX + (targetX - oppositeX) * t1;
              const y1 = oppositeY + (targetY - oppositeY) * t1;
              const x2 = oppositeX + (targetX - oppositeX) * t2;
              const y2 = oppositeY + (targetY - oppositeY) * t2;
              this.aimLineGraphics.lineBetween(x1, y1, x2, y2);
            }
          }
        }
        
        // Power bar
        const dragDistance = Phaser.Math.Distance.Between(
          this.dragStartX,
          this.dragStartY,
          pointer.x,
          pointer.y
        );
        const power = Math.min(1, dragDistance / 150);
        
        const barWidth = 150;
        const barHeight = 15;
        const barX = 640 - barWidth / 2;
        const barY = 640; // Below table bottom (TABLE_OFFSET_Y = 620)
        
        this.powerBarGraphics.fillStyle(0x333333, 0.8);
        this.powerBarGraphics.fillRect(barX, barY, barWidth, barHeight);
        
        const powerColor = power < 0.3 ? 0xff0000 : power < 0.7 ? 0xffff00 : 0x00ff00;
        this.powerBarGraphics.fillStyle(powerColor, 0.9);
        this.powerBarGraphics.fillRect(barX, barY, barWidth * power, barHeight);
        
        this.powerBarGraphics.lineStyle(2, 0xffffff, 1);
        this.powerBarGraphics.strokeRect(barX, barY, barWidth, barHeight);
      }
    });

    this.input.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      if (this.isDragging) {
        this.isDragging = false;
        this.shootCueBall(pointer.x, pointer.y);
        
        if (this.aimLineGraphics) this.aimLineGraphics.clear();
        if (this.powerBarGraphics) this.powerBarGraphics.clear();
      }
      
      if (this.isPositioning) {
        this.isPositioning = false;
      }
    });
  }

  private setupMultiplayer(): void {
    // Clear any stale subscriptions if this scene instance is reused.
    for (const subscription of this.multiplayerSubscriptions) {
      subscription.unsubscribe();
    }
    this.multiplayerSubscriptions = [];

    const menuScene = this.scene.get('MenuScene') as { multiplayerManager?: MultiplayerManager };
    this.multiplayerManager = menuScene?.multiplayerManager ?? MultiplayerManager.getInstance();

    const sessionState = this.multiplayerManager.getSessionState();
    if (!this.multiplayerManager.isInSession() || !sessionState?.isMultiplayer) {
      console.warn('PoolScene: No active multiplayer session found');
      this.isMyTurn = true; // Fallback to single player behavior
      return;
    }

    console.log('[PoolScene] Initialized multiplayer manager reference');

    // Set up multiplayer event listeners
    this.multiplayerSubscriptions.push(
      this.multiplayerManager.getNetworkSync().on('game_state_update', this.onGameStateUpdate.bind(this))
    );
    this.multiplayerSubscriptions.push(
      this.multiplayerManager.getNetworkSync().on('turn_change', this.onTurnChange.bind(this))
    );
    this.gameActionHandler = this.onGameAction.bind(this);
    this.multiplayerManager.onGameAction = this.gameActionHandler;
    this.multiplayerManager.onRestoreFailed = (message: string) => {
      this.handleRestoreFailed(message);
    };

    this.events.once('shutdown', () => {
      for (const subscription of this.multiplayerSubscriptions) {
        subscription.unsubscribe();
      }
      this.multiplayerSubscriptions = [];

      const manager = this.multiplayerManager;
      if (manager && manager.onGameAction === this.gameActionHandler) {
        manager.onGameAction = undefined;
      }
      if (manager) {
        manager.onRestoreFailed = undefined;
      }
      this.gameActionHandler = undefined;
    });
    
    // Get current turn state from authoritative multiplayer session data only.
    this.currentPlayerNumber =
      sessionState?.gameState?.currentPlayer ??
      sessionState?.gameState?.currentPlayerNumber ??
      1;
    this.syncCurrentPlayerToStore(this.currentPlayerNumber);
    this.isMyTurn = sessionState?.playerNumber === this.currentPlayerNumber;

    // Restore any in-flight collected letters from session state (disconnect recovery).
    if (this.isMyTurn && Array.isArray(sessionState?.gameState?.collectedLetters)) {
      this.collectedLetters = [...sessionState.gameState.collectedLetters];
    }

    console.log(`[PoolScene] Multiplayer setup - Player ${sessionState?.playerNumber}, Current turn: ${this.currentPlayerNumber}, My turn: ${this.isMyTurn}`);

    this.updateTurnUI();
  }

  private handleRestoreFailed(message: string): void {
    console.warn('[PoolScene] Session restore failed:', message);
    this.add.text(640, 360, 'Session could not be restored.\nReturning to menu...', {
      fontSize: '24px',
      color: '#ffcc00',
      backgroundColor: '#00000088',
      padding: { x: 16, y: 10 },
      align: 'center',
    }).setOrigin(0.5).setDepth(1000);
    this.time.delayedCall(2500, () => {
      this.scene.start('MenuScene');
    });
  }

  private onGameStateUpdate(payload: GameStateUpdatePayload): void {
    console.log('[PoolScene] Received game state update:', payload);
    
    // Update local game state with synced data
    if (payload.gameState) {
      // Sync ball positions
      this.applyPlayerHandsSnapshotToStore(payload.gameState.playerHands);
      this.applyPlayerScoresSnapshotToStore(payload.gameState.playerScores);
      
      // Sync collected letters.
      // Keep local player's in-turn tally authoritative during their own turn so
      // transient network snapshots do not collapse the top-left "This turn" UI.
      if (Object.prototype.hasOwnProperty.call(payload.gameState, 'collectedLetters')) {
        if (Array.isArray(payload.gameState.collectedLetters)) {
          if (!this.isMyTurn) {
            this.collectedLetters = payload.gameState.collectedLetters;
            this.updateTurnInfoUI();
          }
        } else {
          console.warn('[PoolScene] collectedLetters is not an array, resetting:', payload.gameState.collectedLetters);
          if (!this.isMyTurn) {
            this.collectedLetters = [];
            this.updateTurnInfoUI();
          }
        }
      }
      
      // Sync turn state when provided in game state updates
      const syncedCurrentPlayer = payload.gameState.currentPlayerNumber;
      if (typeof syncedCurrentPlayer === 'number') {
        this.currentPlayerNumber = syncedCurrentPlayer;
        this.syncCurrentPlayerToStore(syncedCurrentPlayer);
        const sessionState = this.multiplayerManager?.getSessionState();
        this.isMyTurn = sessionState?.playerNumber === this.currentPlayerNumber;
      }

      // Sync cue ball position only when this update explicitly changed it.
      // Otherwise stale snapshots (for example BALL_LAUNCH echoes) can
      // incorrectly re-enable cue-ball repositioning mid-turn.
      const changeTypes = Array.isArray(payload.actionResult?.changes)
        ? payload.actionResult.changes
            .map((change: { type?: string }) => change?.type)
            .filter((type): type is string => typeof type === 'string')
        : [];
      const hasChangeMetadata = Array.isArray(payload.actionResult?.changes);
      const cueBallChanged = changeTypes.includes('cue_ball_updated');
      const shouldApplyCueBallState = !hasChangeMetadata || cueBallChanged;
      if (shouldApplyCueBallState && Object.prototype.hasOwnProperty.call(payload.gameState, 'cueBallPosition')) {
        const pos = payload.gameState.cueBallPosition as { x: number; y: number } | null | undefined;
        this.isFirstShot = !pos;
        if (pos && typeof pos.x === 'number' && typeof pos.y === 'number') {
          this.simulation.setCueBallPosition(pos);
        }
      }

      // Remove any balls the active player has pocketed (watcher view only)
      if (!this.isMyTurn && Array.isArray(payload.gameState.currentRackLetters)
          && !payload.actionResult?.changes?.some((c: { type?: string }) =>
              c?.type === 'rack_generated' || c?.type === 'rack_updated')) {
        const remainingIds = new Set(
          (payload.gameState.currentRackLetters as Array<{ letter: { id: string } }>).map(rl => rl.letter.id)
        );
        for (const data of [...this.letterBallData]) {
          if (!remainingIds.has(data.letter.id)) {
            this.removeBall(data.id);
          }
        }
      }

      // Only rebuild rack when we know the rack actually changed.
      // Rebuilding on every BALL_LAUNCH update can snap balls back mid-shot.
      const hasRackInPayload = Array.isArray(payload.gameState.currentRackLetters);
      const hasNonEmptyRackInPayload =
        hasRackInPayload &&
        (payload.gameState.currentRackLetters as Array<{ letter: Letter; x: number; y: number }>).length > 0;
      const hasLocalRack = this.letterBallData.length > 0;
      const rackChanged = !!payload.actionResult?.changes?.some(
        (change: { type?: string }) => change?.type === 'rack_generated' || change?.type === 'rack_updated'
      );
      const shouldRebuildRack = rackChanged || (!hasLocalRack && hasNonEmptyRackInPayload);

      if (hasRackInPayload && shouldRebuildRack) {
        this.rebuildLetterRack(payload.gameState.currentRackLetters as Array<{ letter: Letter; x: number; y: number }>);
        this.renderLetterBalls();
      }
    }
    
    // Update UI
    this.updateTurnUI();
  }

  private onTurnChange(payload: TurnChangePayload): void {
    console.log('[PoolScene] Turn changed:', payload);

    // A new turn should always clear any lingering turn-end prompt from a prior turn.
    this.clearPendingTurnEndUI();
    
    this.currentPlayerNumber = payload.currentPlayerNumber;
    this.syncCurrentPlayerToStore(this.currentPlayerNumber);
    const sessionState = this.multiplayerManager?.getSessionState();
    this.isMyTurn = sessionState?.playerNumber === this.currentPlayerNumber;

    if (Array.isArray(payload.currentRackLetters)) {
      this.rebuildLetterRack(payload.currentRackLetters as Array<{ letter: Letter; x: number; y: number }>);
      this.renderLetterBalls();
    }

    if (this.multiplayerManager?.isInSession() && this.isMyTurn
        && Array.isArray(payload.currentRackLetters) && this.letterBallData.length === 0) {
      this.setupMultiplayerLetterBalls();
    }

    this.applyPlayerHandsSnapshotToStore(payload.playerHands);
    this.applyPlayerScoresSnapshotToStore(payload.playerScores);

    if (payload.letterPool || payload.consecutivePassesPerPlayer) {
      const storeState = useGameStore.getState();
      if (storeState.game) {
        storeState.setGame({
          ...storeState.game,
          ...(payload.letterPool ? { letterPool: payload.letterPool } : {}),
          ...(payload.consecutivePassesPerPlayer ? { consecutivePassesPerPlayer: payload.consecutivePassesPerPlayer } : {}),
          updatedAt: new Date(),
        });
      }
    }

    // Propagate cue-ball position from turn_change into session state so that
    // getSessionCueBallPosition() returns the fresh value when placing the ball.
    if (sessionState?.gameState && Object.prototype.hasOwnProperty.call(payload, 'cueBallPosition')) {
      sessionState.gameState.cueBallPosition = payload.cueBallPosition ?? null;
    }

    // Re-derive whether cue-ball repositioning is allowed for the incoming turn.
    const sessionCueBallPosition = this.getSessionCueBallPosition();
    if (sessionCueBallPosition !== undefined) {
      this.isFirstShot = sessionCueBallPosition === null;
      if (this.isMyTurn) {
        if (sessionCueBallPosition && typeof sessionCueBallPosition.x === 'number' && typeof sessionCueBallPosition.y === 'number') {
          this.simulation.setCueBallPosition(sessionCueBallPosition);
        } else if (sessionCueBallPosition === null) {
          // Scratch handoff: always reset to the foot spot so reposition drag starts on the baseline.
          this.simulation.setCueBallPosition(this.getDefaultCueBallPosition());
        }
      }
      if (!this.isFirstShot) {
        this.isPositioning = false;
      }
    }


    // A new turn has begun. Do not carry the previous player's collected letters
    // into the next player's pool or board flow.
    this.collectedLetters = [];
    this.lettersThisShot = 0;
    this.gutteredLetters = [];
    this.gutteredThisTurn = 0;
    this.lastBallClearedByGutterThisShot = false;
    this.updateTurnInfoUI();

    // Turn ownership flipped: keep local simulation flags in sync with authority.
    this.turnActive = this.isMyTurn;
    this.isShooting = false;
    if (this.isMyTurn) {
      this.scratchDetected = false;
      this.ballsInGutters.clear();
      if (this.cueBallSprite) {
        this.cueBallSprite.setVisible(true);
      }
    }
    
    if (payload.sessionPhase === 'FINISHED') {
      this.scene.start('GameOverScene');
      return;
    }

    console.log(`[PoolScene] Turn change - Current: ${this.currentPlayerNumber}, My turn: ${this.isMyTurn}`);

    // If a fresh rack was just generated this turn, override the isFirstShot derivation above:
    // the cue ball must start on the baseline and be repositionable, regardless of saved position.
    if (this.freshRackGenerated) {
      this.freshRackGenerated = false;
      this.isFirstShot = true;
      this.isPositioning = true;
      const defaultPos = this.getDefaultCueBallPosition();
      this.simulation.setCueBallPosition(defaultPos);
      const screenPos = this.physicsToScreen(defaultPos.x, defaultPos.y);
      this.cueBallSprite?.setPosition(screenPos.x, screenPos.y);
      console.log('🎱 Fresh rack: cue ball reset to starting line, repositioning enabled');
    }

    this.updateTurnUI();

    // Show turn change notification
    this.showTurnNotification(payload);
  }

  private onGameAction(actionType: string, data: any): void {
    console.log('[PoolScene] Processing game action:', actionType, data);
    
    switch (actionType) {
      case GameActionType.BALL_LAUNCH:
        this.syncBallLaunch(data);
        break;
      case GameActionType.BALL_AIM:
        this.syncBallAim(data);
        break;
      case ('RACK_GENERATED' as string):
        console.log('[PoolScene] *** PROCESSING RACK_GENERATED ACTION ***');
        this.syncRackGenerated(data);
        break;
      default:
        console.log('[PoolScene] Unknown game action type:', actionType);
    }
  }

  private syncBallLaunch(data: { velocity: { x: number; y: number }, angularVelocity: number }): void {
    if (!this.isMyTurn) {
      console.log('[PoolScene] Syncing ball launch from other player');
      this.simulation.launchCueBall(data.velocity, data.angularVelocity);
      this.isShooting = true;
      this.prevBallPositions.clear();
      this.ballPositionHistory.clear();
    }
  }

  private syncBallAim(data: { startX: number; startY: number; endX: number; endY: number }): void {
    // Show other player's aim line briefly
    if (!this.isMyTurn) {
      this.aimLineGraphics.clear();
      this.aimLineGraphics.lineStyle(2, 0xff6600, 0.6); // Orange for other player
      this.aimLineGraphics.lineBetween(data.startX, data.startY, data.endX, data.endY);
      
      // Clear after 1 second
      this.time.delayedCall(1000, () => {
        this.aimLineGraphics.clear();
      });
    }
  }

  private syncRackGenerated(data: { rackLetters: any[], updatedPool: any, generatorPlayerNumber?: number }): void {
    console.log('[PoolScene] *** ENTERING syncRackGenerated ***');
    const sessionState = this.multiplayerManager?.getSessionState();
    console.log('[PoolScene] My player number:', sessionState?.playerNumber);

    if (sessionState?.playerNumber === data.generatorPlayerNumber) {
      console.log('[PoolScene] Skipping rack sync - I generated this rack');
      return;
    }

    console.log('[PoolScene] Syncing shared rack from player', data.generatorPlayerNumber, ':', data.rackLetters.length, 'letters');
    console.log('[PoolScene] Rack letters:', data.rackLetters.map(r => r.letter).join(''));

    if (data.updatedPool) {
      const store = useGameStore.getState();
      if (store.game) {
        store.setGame({ ...store.game, letterPool: data.updatedPool, updatedAt: new Date() });
      }
    }

    this.rebuildLetterRack(data.rackLetters);

    // Re-render the letter balls
    this.renderLetterBalls();
  }

  private rebuildLetterRack(rackLetters: Array<{ letter: Letter; x: number; y: number }>): void {
    for (const ballData of this.letterBallData) {
      this.simulation.removeLetterBall(ballData.id);
    }

    for (const sprite of this.letterBallSprites.values()) {
      sprite.destroy();
    }
    for (const text of this.letterTexts.values()) {
      text.destroy();
    }
    this.letterBallSprites.clear();
    this.letterTexts.clear();

    // Ball IDs are reassigned sequentially (ball-1, ball-2, …) each time the rack is
    // rebuilt. Without resetting these, old pocketed IDs or stale positions from a
    // prior rack would collide with the new IDs and make those balls permanently
    // undetectable or produce bogus swept-path checks.
    this.pocketDetector?.reset();
    this.prevBallPositions?.clear();

    this.letterBallData = rackLetters.map((rackLetter, index) => ({
      id: `ball-${index + 1}`,
      letter: rackLetter.letter,
      ballNumber: index + 1,
    }));

    const rackPositionMap = new Map<string, { x: number; y: number }>();
    rackLetters.forEach((rackLetter, index) => {
      rackPositionMap.set(`ball-${index + 1}`, { x: rackLetter.x, y: rackLetter.y });
    });

    this.simulation.createLetterBallsAtPositions(this.letterBallData, rackPositionMap);
    this.updateRackInfoUI();
  }

  // Public method for MultiplayerManager to forward game actions
  public handleMultiplayerGameAction(actionType: string, actionData: any): void {
    console.log('[PoolScene] *** RECEIVED GAME ACTION FROM MULTIPLAYER MANAGER ***');
    console.log('[PoolScene] Action type:', actionType);
    console.log('[PoolScene] Action data:', actionData);
    this.onGameAction(actionType, actionData);
  }

  private updateTurnUI(): void {
    // Destroy existing turn indicator
    if (this.turnIndicatorText) {
      this.turnIndicatorText.destroy();
      this.turnIndicatorText = undefined;
    }
    if (this.playerStatusText) {
      this.playerStatusText.destroy();
      this.playerStatusText = undefined;
    }
    
    // Update scoreboard highlighting for new current player
    this.updateScoreboardHighlighting();
    this.updatePlayerHandUI();
    
    // Create turn indicator
    const sessionState = this.multiplayerManager?.getSessionState();
    if (sessionState && sessionState.isMultiplayer) {
      const currentPlayerName = sessionState.players.find(p => p.playerNumber === this.currentPlayerNumber)?.displayName || `Player ${this.currentPlayerNumber}`;
      
      if (this.isMyTurn) {
        this.turnIndicatorText = this.add.text(640, 60, 'Your Turn', {
          fontSize: '24px',
          color: '#00ff00',
          backgroundColor: '#002200',
          padding: { x: 12, y: 6 },
          fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
        }).setOrigin(0.5).setDepth(100);
        
        this.playerStatusText = this.add.text(640, 85, 'Take your shot!', {
          fontSize: '14px',
          color: '#88ff88',
          fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
        }).setOrigin(0.5).setDepth(100);
      } else {
        this.turnIndicatorText = this.add.text(640, 60, `${currentPlayerName}'s Turn`, {
          fontSize: '24px',
          color: '#ffaa00',
          backgroundColor: '#332200',
          padding: { x: 12, y: 6 },
          fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
        }).setOrigin(0.5).setDepth(100);
        
        this.playerStatusText = this.add.text(640, 85, 'Please wait...', {
          fontSize: '14px',
          color: '#ffcc88',
          fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
        }).setOrigin(0.5).setDepth(100);
      }
    }
  }

  /**
   * Update scoreboard highlighting when turn changes
   * Refreshes card styling and text color for current player
   */
  private updateScoreboardHighlighting(): void {
    const store = useGameStore.getState();
    const game = store.game;
    if (!game) return;
    
    const sessionState = this.multiplayerManager?.getSessionState();
    if (!sessionState || !sessionState.isMultiplayer) return;
    
    // Update each player's scoreboard card
    game.players.forEach((_player, index) => {
      const playerNumber = index + 1;
      const isCurrent = playerNumber === this.currentPlayerNumber;
      const scoreCard = this.scoreboardCards.get(playerNumber);
      const scoreText = this.scoreboardTexts.get(playerNumber);
      
      if (scoreCard) {
        // Clear and redraw card with new styling
        scoreCard.clear();
        scoreCard.fillStyle(isCurrent ? COLORS.primary.main : COLORS.background.tertiary, isCurrent ? 0.3 : 0.6);
        scoreCard.fillRoundedRect(1060, 18 + index * 40, 200, 32, 6);
        if (isCurrent) {
          scoreCard.lineStyle(2, COLORS.primary.main, 1);
          scoreCard.strokeRoundedRect(1060, 18 + index * 40, 200, 32, 6);
        }
      }
      
      if (scoreText) {
        scoreText.setStyle({
          color: isCurrent ? COLORS.text.accent : COLORS.text.primary,
        });
      }
    });
  }

  private showTurnNotification(payload: TurnChangePayload): void {
    const sessionState = this.multiplayerManager?.getSessionState();
    if (!sessionState) return;
    
    const nextPlayerName = sessionState.players.find(p => p.playerNumber === payload.nextPlayerNumber)?.displayName || `Player ${payload.nextPlayerNumber}`;
    const isNextPlayerMe = sessionState.playerNumber === payload.nextPlayerNumber;
    
    let message = '';
    if (isNextPlayerMe) {
      message = 'Your turn!';
    } else {
      message = `${nextPlayerName}'s turn`;
    }
    
    // Show floating notification
    const notification = this.add.text(640, 200, message, {
      fontSize: '32px',
      color: isNextPlayerMe ? '#00ff00' : '#ffaa00',
      backgroundColor: isNextPlayerMe ? '#003300' : '#332200',
      padding: { x: 20, y: 10 },
      fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
    }).setOrigin(0.5).setDepth(200);
    
    // Animate and remove
    this.tweens.add({
      targets: notification,
      y: 150,
      alpha: 0,
      duration: 2000,
      ease: 'Power2.easeOut',
      onComplete: () => {
        notification.destroy();
      }
    });
  }

  private sendGameAction(actionType: GameActionType, data: any): void {
    if (this.multiplayerManager) {
      if (this.isMyTurn) {
        console.log('[PoolScene] Sending game action:', actionType, { isMyTurn: this.isMyTurn, actionType });
        this.multiplayerManager.sendGameAction(actionType, data);
      } else {
        console.log('[PoolScene] Skipping game action (not my turn):', actionType);
      }
    }
  }

  private setupMultiplayerLetterBalls(): void {
    console.log('[PoolScene] Setting up multiplayer letter balls');
    
    const sessionState = this.multiplayerManager!.getSessionState();
    if (!sessionState || !sessionState.gameState) {
      console.warn('[PoolScene] No multiplayer session or game state found, generating rack');
      // Still generate a rack for the first player (by player number, not turn)
      if (sessionState?.playerNumber === 1) {
        this.generateSharedRack();
      }
      return;
    }
    
    const gameState = sessionState.gameState;
    console.log('[PoolScene] Multiplayer game state:', { 
      hasRack: !!gameState.currentRackLetters,
      rackLength: gameState.currentRackLetters?.length || 0,
      currentPlayer: gameState.currentPlayer ?? gameState.currentPlayerNumber,
      myPlayerNumber: sessionState.playerNumber,
      totalPlayers: sessionState.players?.length || 0
    });
    
    // Sanity check: reject stale/invalid turn numbers
    const currentPlayerFromState = gameState.currentPlayer ?? gameState.currentPlayerNumber ?? 1;
    const isValidTurn = currentPlayerFromState >= 1 && currentPlayerFromState <= (sessionState.players?.length || 2);
    if (!isValidTurn) {
      console.warn(`[PoolScene] Invalid turn number ${currentPlayerFromState}, resetting to 1`);
      gameState.currentPlayer = 1;
      gameState.currentPlayerNumber = 1;
    }
    
    // Use shared rack from multiplayer session
    if (Array.isArray(gameState.currentRackLetters) && gameState.currentRackLetters.length > 0) {
      console.log(`[PoolScene] Using shared rack with ${gameState.currentRackLetters.length} letters`);
      const normalizedRack = gameState.currentRackLetters.map((rackLetter: any, index: number) => ({
        letter: rackLetter.letter,
        x: rackLetter.x ?? this.getTrianglePosition(index).x,
        y: rackLetter.y ?? this.getTrianglePosition(index).y,
      }));
      this.rebuildLetterRack(normalizedRack);
      
      // Clear any pending timeout since we got the rack
      if (this.rackGenerationTimer) {
        clearTimeout(this.rackGenerationTimer);
        this.rackGenerationTimer = null;
      }
      
    } else {
      console.log('[PoolScene] No shared rack in session state');

      // ONLY use local persisted rack in single-player (never in multiplayer where server is authority)
      // In multiplayer, wait for Player 1 to generate or timeout
      if (!sessionState.isMultiplayer) {
        const localGame = useGameStore.getState().game;
        if (localGame && localGame.currentRackLetters.length > 0) {
          console.log(`[PoolScene] Single-player: Using local persisted rack with ${localGame.currentRackLetters.length} letters`);
          this.rebuildLetterRack(localGame.currentRackLetters);
          return;
        }
      }

      console.log('[PoolScene] Checking who should generate the rack');
      const rackOwnerPlayerNumber = gameState.currentPlayer ?? gameState.currentPlayerNumber ?? 1;
      if (sessionState.playerNumber === rackOwnerPlayerNumber && this.isMyTurn) {
        console.log(`[PoolScene] I am turn owner (player ${rackOwnerPlayerNumber}), generating shared rack`);
        this.generateSharedRack();
      } else {
        console.log('[PoolScene] Waiting for turn owner to generate rack (will timeout in ' + this.RACK_GENERATION_TIMEOUT + 'ms)', {
          rackOwnerPlayerNumber,
          myPlayerNumber: sessionState.playerNumber,
        });
        
        // Safety timeout: only the current turn owner can generate backup.
        this.rackGenerationTimer = setTimeout(() => {
          const currentState = this.multiplayerManager!.getSessionState();
          const currentGameState = currentState?.gameState;
          const currentOwner = currentGameState?.currentPlayer ?? currentGameState?.currentPlayerNumber ?? 1;
          const iAmCurrentOwner = currentState?.playerNumber === currentOwner;
          
          // Check if rack is still empty
          if (!currentGameState?.currentRackLetters || currentGameState.currentRackLetters.length === 0) {
            if (iAmCurrentOwner && this.isMyTurn) {
              console.warn('[PoolScene] Timeout waiting for rack - current turn owner generating backup rack');
              this.generateSharedRack();
            } else {
              console.log('[PoolScene] Timeout reached, but this client is not eligible to generate rack backup', {
                currentOwner,
                myPlayerNumber: currentState?.playerNumber,
                isMyTurn: this.isMyTurn,
              });
            }
          } else {
            console.log('[PoolScene] Rack arrived just in time before timeout');
          }
          
          this.rackGenerationTimer = null;
        }, this.RACK_GENERATION_TIMEOUT);
      }
    }
  }
  
  private generateSharedRack(): void {
    console.log('[PoolScene] Generating shared letter rack for multiplayer');
    
    const sessionState = this.multiplayerManager?.getSessionState();
    console.log('[PoolScene] Session state for rack generation:', {
      playerNumber: sessionState?.playerNumber,
      sessionId: sessionState?.sessionId,
      isMultiplayer: sessionState?.isMultiplayer
    });
    
    // This would normally use the same letter generation logic
    // For now, create a simple shared rack and broadcast it
    const store = useGameStore.getState();
    const game = store.game;
    
    if (!game) {
      console.error('[PoolScene] No game state found for rack generation');
      return;
    }
    
    // Generate 15 letters for the rack
    const { letters: drawnLetters, updatedPool } = drawRandomLetters(game.letterPool, 15, 4);
    
    console.log('[PoolScene] Generated letters for shared rack:', drawnLetters.map(l => l.character).join(''));
    
    // Create rack positions
    const rackLetters = drawnLetters.map((letter, index) => ({
      letter,
      x: this.getTrianglePosition(index).x,
      y: this.getTrianglePosition(index).y
    }));
    
    console.log('[PoolScene] About to send RACK_GENERATED action...');
    
    // Send the rack to all players via game action
    this.sendGameAction('RACK_GENERATED' as any, {
      rackLetters,
      updatedPool,
      generatorPlayerNumber: sessionState?.playerNumber,
    });
    
    // Persist the drawn-down pool so the local store stays in sync.
    store.setGame({ ...game, letterPool: updatedPool, updatedAt: new Date() });

    // Fresh rack = fresh break: clear saved cue ball position so renderCueBall uses the default.
    const sessionState2 = this.multiplayerManager?.getSessionState();
    if (sessionState2?.gameState) {
      sessionState2.gameState.cueBallPosition = null;
    }
    this.isFirstShot = true;
    this.isPositioning = true;
    this.freshRackGenerated = true;

    // Also apply the rack locally for the player who generated it
    this.rebuildLetterRack(rackLetters);

    // Re-render the letter balls
    this.renderLetterBalls();
  }
  
  private getTrianglePosition(index: number): { x: number, y: number } {
    const layout = getBallPosition(index + 1);
    if (!layout) {
      return { x: RACK_CENTER.x, y: RACK_CENTER.y };
    }

    return {
      x: RACK_CENTER.x + layout.position.x,
      y: RACK_CENTER.y + layout.position.y,
    };
  }

  private shootCueBall(targetX: number, targetY: number): void {
    if (!this.cueBallSprite) return;
    
    const oppositeX = this.cueBallSprite.x - (targetX - this.cueBallSprite.x);
    const oppositeY = this.cueBallSprite.y - (targetY - this.cueBallSprite.y);
    
    const angle = this.ballLauncher.calculateAngleFromTarget(
      this.cueBallSprite.x,
      this.cueBallSprite.y,
      oppositeX,
      oppositeY
    );
    
    const dragDistance = Phaser.Math.Distance.Between(
      this.dragStartX,
      this.dragStartY,
      targetX,
      targetY
    );
    
    const power = this.ballLauncher.calculatePowerFromDistance(dragDistance, 150);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const velocity = this.ballLauncher.calculateVelocity(angle, power, { maxVelocity: 15.0 } as any);
    
    // Invert Y for screen coords
    velocity.y = -velocity.y;
    
    // Calculate spin from horizontal offset
    const horizontalOffset = targetX - this.cueBallSprite.x;
    const spin = -horizontalOffset * 0.008;
    const angularVelocity = Math.max(-2.0, Math.min(2.0, spin));
    
    this.simulation.launchCueBall(velocity, angularVelocity);
    this.isShooting = true;
    this.isFirstShot = false; // After first shot, no more repositioning
    this.lettersThisShot = 0; // Reset for this shot
    this.lastBallClearedByGutterThisShot = false;
    this.cueBallHitAnyBall = false; // Reset hit tracker for this shot
    this.ballsInGutters.clear(); // Reset gutter tracking for new shot
    this.prevBallPositions.clear(); // Reset swept-detection history for new shot
    this.ballPositionHistory.clear(); // Reset stuck-ball history for new shot

    // Send game action to other players
    this.sendGameAction(GameActionType.BALL_LAUNCH, {
      velocity,
      angularVelocity
    });
    
    console.log('Shot:', velocity, 'spin:', angularVelocity);
  }

  update(time: number, delta: number): void {
    this.simulation.update(delta);
    this.syncSpritesWithPhysics();
    this.checkPockets();
    
    // Check for stuck balls periodically
    if (this.isShooting && time - this.lastStuckCheck > this.STUCK_CHECK_INTERVAL) {
      this.checkStuckBalls(time);
      this.lastStuckCheck = time;
    }
    
    if (this.isShooting && this.simulation.areAllBallsStopped()) {
      this.isShooting = false;

      // Remote clients should mirror motion only; authoritative turn resolution
      // happens on the active player's client and is broadcast by the server.
      if (!this.isMyTurn) {
        return;
      }
      
      // If scratch was detected, handle it now that all balls have stopped
      if (this.scratchDetected) {
        console.log('All balls stopped after scratch - ending turn');
        this.onCueBallScratched();
        return;
      }
      
      this.onShotComplete();
    }
  }
  private syncSpritesWithPhysics(): void {
    // Sync cue ball
    const cueBall = this.simulation.getCueBall();
    if (cueBall && this.cueBallSprite) {
      const screenPos = this.physicsToScreen(cueBall.x, cueBall.y);
      this.cueBallSprite.setPosition(screenPos.x, screenPos.y);
    }
    
    // Sync letter balls
    for (const [ballId, sprite] of this.letterBallSprites.entries()) {
      const ballBody = this.simulation.getLetterBall(ballId);
      if (ballBody) {
        const screenPos = this.physicsToScreen(ballBody.x, ballBody.y);
        sprite.setPosition(screenPos.x, screenPos.y);
        
        const text = this.letterTexts.get(ballId);
        if (text) {
          text.setPosition(screenPos.x, screenPos.y);
        }
      }
    }
  }

  private checkPockets(): void {
    // Check cue ball
    const cueBall = this.simulation.getCueBall();
    if (cueBall && this.isShooting) {
      // Check if cue ball is completely out of bounds (fell off table)
      const OUT_OF_BOUNDS_THRESHOLD = 1.0; // 1 meter beyond table edges
      const maxX = 1.2 + OUT_OF_BOUNDS_THRESHOLD; // TABLE_WIDTH/2 + threshold
      const maxY = 5.0 + OUT_OF_BOUNDS_THRESHOLD; // TABLE_LENGTH + threshold
      const minY = -OUT_OF_BOUNDS_THRESHOLD;
      
      const isOutOfBounds = 
        Math.abs(cueBall.x) > maxX || 
        cueBall.y > maxY || 
        cueBall.y < minY;
      
      if (isOutOfBounds && !this.ballsInGutters.has('cue-ball')) {
        console.log('Cue ball out of bounds - scratch detected, waiting for balls to stop', { x: cueBall.x, y: cueBall.y });
        this.ballsInGutters.add('cue-ball');
        this.scratchDetected = true;
        // Stop the cue ball immediately
        cueBall.vx = 0;
        cueBall.vy = 0;
        // Hide the cue ball sprite
        if (this.cueBallSprite) {
          this.cueBallSprite.setVisible(false);
        }
        return;
      }
      
      const cueBallPos = { x: cueBall.x, y: cueBall.y };
      const pocket = this.pocketDetector.checkBallInPocket(
        'cue-ball', cueBallPos, this.prevBallPositions.get('cue-ball'),
      );
      this.prevBallPositions.set('cue-ball', cueBallPos);
      if (pocket && !this.scratchDetected) {
        console.log('Cue ball pocketed - scratch detected, waiting for balls to stop');
        this.scratchDetected = true;
        // Stop the cue ball immediately
        cueBall.vx = 0;
        cueBall.vy = 0;
        // Hide the cue ball sprite
        if (this.cueBallSprite) {
          this.cueBallSprite.setVisible(false);
        }
      }
      
      if (!this.ballsInGutters.has('cue-ball')) {
        const gutter = this.pocketDetector.checkBallInGutter({ x: cueBall.x, y: cueBall.y });
        if (gutter && !this.scratchDetected) {
          console.log('Cue ball in gutter - scratch detected, waiting for balls to stop');
          this.ballsInGutters.add('cue-ball');
          this.scratchDetected = true;
          // Stop ball in gutter
          cueBall.vx = 0;
          cueBall.vy = 0;
          // Hide the cue ball sprite
          if (this.cueBallSprite) {
            this.cueBallSprite.setVisible(false);
          }
        }
      }
    }
    
    // Check letter balls
    for (const data of this.letterBallData) {
      const ballBody = this.simulation.getLetterBall(data.id);
      if (ballBody) {
        // Check if letter ball is completely out of bounds (remove it).
        // Side threshold: 1.0m gives plenty of margin for the gutter zone.
        // Back threshold: 0.3m — back pockets have a capture radius of ~0.19m, so
        // any ball more than 0.3m past the back wall has escaped through the cushion
        // and will never be caught by a pocket. Treat it as guttered.
        const SIDE_OOB = 1.0;
        const BACK_OOB = 0.3;
        const maxX = 1.2 + SIDE_OOB;
        const maxY = 5.0 + BACK_OOB;
        const minY = -SIDE_OOB;
        
        const isOutOfBounds = 
          Math.abs(ballBody.x) > maxX || 
          ballBody.y > maxY || 
          ballBody.y < minY;
        
        if (isOutOfBounds && !this.ballsInGutters.has(data.id)) {
          console.log(`Letter ball ${data.id} out of bounds - will return to pool`, { x: ballBody.x, y: ballBody.y });
          this.ballsInGutters.add(data.id);
          this.gutteredLetters.push(data.letter); // Track for return to pool
          this.markLastBallGutterIfRackNowEmpty();
          this.removeBall(data.id);
          continue;
        }
        
        // Only check pockets during an active shot. Without this guard a ball whose
        // saved position falls inside the detection zone would be collected on the very
        // first physics tick of a new scene (before any shot is fired), losing the
        // letter without the player earning it.
        const ballPos = { x: ballBody.x, y: ballBody.y };
        if (this.isShooting) {
          this.pocketDetector.checkBallInPocket(
            data.id, ballPos, this.prevBallPositions.get(data.id),
          );
        }
        this.prevBallPositions.set(data.id, ballPos);

        if (!this.ballsInGutters.has(data.id)) {
          const gutter = this.pocketDetector.checkBallInGutter({ x: ballBody.x, y: ballBody.y });
          if (gutter) {
            console.log(`Letter ball ${data.id} in gutter - will return to pool`, { x: ballBody.x, y: ballBody.y });
            this.ballsInGutters.add(data.id);
            // Stop ball in gutter
            ballBody.vx = 0;
            ballBody.vy = 0;
            // Track for return to pool
            this.gutteredLetters.push(data.letter);
            this.gutteredThisTurn++;
            this.markLastBallGutterIfRackNowEmpty();
            // Remove after a brief moment so player can see what happened
            this.time.delayedCall(500, () => this.removeBall(data.id));
          }
        }
      }
    }
  }
  
  /**
   * Check for balls that are stuck at table edges or on rails
   */
  private checkStuckBalls(currentTime: number): void {
    for (const data of this.letterBallData) {
      if (this.ballsInGutters.has(data.id)) continue; // Already handled
      
      const ballBody = this.simulation.getLetterBall(data.id);
      if (!ballBody) continue;
      
      // Initialize position history for this ball
      if (!this.ballPositionHistory.has(data.id)) {
        this.ballPositionHistory.set(data.id, []);
      }
      
      const history = this.ballPositionHistory.get(data.id)!;
      const currentPos = { x: ballBody.x, y: ballBody.y, time: currentTime };
      
      // Add current position to history
      history.push(currentPos);
      
      // Keep only recent positions (last 5 seconds)
      const recentHistory = history.filter(pos => currentTime - pos.time < 5000);
      this.ballPositionHistory.set(data.id, recentHistory);
      
      // Check if ball has been stuck for a while
      if (recentHistory.length >= 3) {
        const oldestPos = recentHistory[0];
        const timeDiff = currentTime - oldestPos.time;
        
        if (timeDiff >= this.STUCK_TIME_THRESHOLD) {
          const distance = Math.sqrt(
            Math.pow(currentPos.x - oldestPos.x, 2) + 
            Math.pow(currentPos.y - oldestPos.y, 2)
          );
          
          if (isStuckOnRail(ballBody.x, distance, TABLE_WIDTH / 2)) {
            console.log(`Ball ${data.id} stuck on rail - treating as guttered`,
              { x: ballBody.x, y: ballBody.y, distance });

            this.ballsInGutters.add(data.id);
            ballBody.vx = 0;
            ballBody.vy = 0;
            this.gutteredLetters.push(data.letter);
            this.gutteredThisTurn++;
            this.markLastBallGutterIfRackNowEmpty();
            this.removeBall(data.id);
            this.ballPositionHistory.delete(data.id);
          }
        }
      }
    }
  }

  private onBallPocketed(ballId: string, pocketName: string): void {
    console.log(`Ball ${ballId} pocketed in ${pocketName}`);

    // Don't collect letters if turn has ended (e.g., due to scratch)
    if (!this.turnActive) {
      console.log('Turn already ended, letter not collected');
      return;
    }

    // Don't collect letters outside of an active shot. checkPockets() already guards
    // the checkBallInPocket call with isShooting, so this is defense-in-depth only.
    if (!this.isShooting) {
      console.log(`[PoolScene] Ball ${ballId} in pocket zone but no shot in progress — not collecting`);
      return;
    }
    
    const data = this.letterBallData.find(d => d.id === ballId);
    if (data) {
      this.collectedLetters.push(data.letter);
      this.lettersThisShot++; // Track this shot's collection
      this.removeBall(ballId);
      this.updateTurnInfoUI();
      this.syncTurnProgress();
      console.log('Collected letter:', data.letter.character);
    }
  }

  private syncTurnProgress(includeTableState = false): void {
    if (!this.multiplayerManager || !this.isMultiplayerSessionActive() || !this.isMyTurn || !this.turnActive) {
      return;
    }

    const actionData: Record<string, unknown> = {
      collectedLetters: this.collectedLetters,
    };

    if (includeTableState) {
      const cueBall = this.simulation.getCueBall();
      actionData.cueBallPosition = cueBall ? { x: cueBall.x, y: cueBall.y } : null;

      const pocketedIds = new Set(this.collectedLetters.map(l => l.id));
      const gutteredIds = new Set(this.gutteredLetters.map(l => l.id));
      const remainingRack: RackLetter[] = [];
      for (const data of this.letterBallData) {
        if (!pocketedIds.has(data.letter.id) && !gutteredIds.has(data.letter.id)) {
          const ball = this.simulation.getLetterBall(data.id);
          if (ball) remainingRack.push({ letter: data.letter, x: ball.x, y: ball.y });
        }
      }
      actionData.currentRackLetters = remainingRack;
    }

    this.sendGameAction(GameActionType.TURN_PROGRESS, actionData);
  }

  private onCueBallScratched(): void {
    console.log('Scratch! Cue ball pocketed/guttered - turn ends');
    
    // Cue ball should already be stopped and hidden from checkPockets
    this.endTurn(true);
  }

  private removeBall(ballId: string): void {
    const sprite = this.letterBallSprites.get(ballId);
    if (sprite) {
      sprite.destroy();
      this.letterBallSprites.delete(ballId);
    }
    
    const text = this.letterTexts.get(ballId);
    if (text) {
      text.destroy();
      this.letterTexts.delete(ballId);
    }
    
    this.simulation.removeLetterBall(ballId);
    
    // Remove from letterBallData to keep rack count accurate
    const index = this.letterBallData.findIndex(d => d.id === ballId);
    if (index !== -1) {
      this.letterBallData.splice(index, 1);
    }
  }

  private onShotComplete(): void {
    console.log('Shot complete');
    
    // Check if cue ball hit any letter balls (only relevant if no scratch detected)
    if (!this.cueBallHitAnyBall && this.turnActive && !this.scratchDetected) {
      console.log('NO HIT! Cue ball did not contact any letter balls - scratch!');
      this.onCueBallScratched();
      return;
    }
    
    // Check if rack is now cleared (all balls pocketed or guttered) - check this regardless of whether balls were pocketed THIS shot
    if (this.turnActive) {
      const remainingBalls = this.getRemainingRackBallData();
      
      if (remainingBalls.length === 0) {
        console.log('🎱 RACK CLEARED! All balls pocketed/guttered.');

        // House rule: if the final unresolved rack ball was guttered, end the turn.
        // Do not award a fresh continuation rack for this shooter.
        if (this.lastBallClearedByGutterThisShot) {
          console.log('🎱 Last unresolved rack ball was guttered - ending turn without fresh rack continuation.');
          // Clear stale session rack so resolveRackForTurnHandoff in BoardScene
          // does not pick up the ghost ball from the prior turn_change payload.
          const sessionState = this.multiplayerManager?.getSessionState?.();
          if (sessionState?.gameState) {
            sessionState.gameState.currentRackLetters = [];
          }
          this.endTurn(false);
          return;
        }
        
        // Check if adding letters would exceed MAX_HAND_SIZE
        const store = useGameStore.getState();
        let game = store.game!;
        const currentPlayer = getCurrentPlayer(game);
        const totalLetters = currentPlayer.hand.length + this.collectedLetters.length;
        
        if (totalLetters > this.MAX_HAND_SIZE) {
          console.log(`Rack cleared but would exceed ${this.MAX_HAND_SIZE} letter limit. Turn ends.`);
          this.endTurn(false);
          return;
        }
        
        console.log('Setting up fresh rack - player continues!');
        
        // Add collected letters to hand immediately
        const updatedPlayer = addLettersToHand(currentPlayer, this.collectedLetters);
        game = updatePlayer(game, currentPlayer.id, updatedPlayer);
        
        // Handle guttered letters according to rules
        if (game.rules.returnGutteredLetters && this.gutteredLetters.length > 0) {
          console.log(`♻️ Returning ${this.gutteredLetters.length} guttered letters to pool:`, 
                      this.gutteredLetters.map(l => l.character).join(', '));
          const updatedPool = returnLetters(game.letterPool, this.gutteredLetters);
          game = { ...game, letterPool: updatedPool };
        } else if (!game.rules.returnGutteredLetters && this.gutteredLetters.length > 0) {
          console.log(`🗑️ Guttered letters permanently lost (${this.gutteredLetters.length}):`, 
                      this.gutteredLetters.map(l => l.character).join(', '));
        }
        
        // Clear the rack and reset cue ball position for fresh start
        game = {
          ...game,
          currentRackLetters: [], // Empty rack triggers fresh 15-letter rack
          cueBallPosition: null,  // Allow repositioning
        };
        store.setGame(game);

        // Clear stale session rack so the new scene generates a fresh 15-ball rack
        // instead of placing the 1-ball remnant from the prior turn_change payload.
        const sessionState = this.multiplayerManager?.getSessionState?.();
        if (sessionState?.gameState) {
          sessionState.gameState.currentRackLetters = [];
        }

        console.log(`🎱 Added ${this.collectedLetters.length} letters to hand, restarting with fresh rack`);

        // Restart scene to set up fresh rack
        this.scene.restart();
        return;
      }
    }
    
    // Check if we sunk any balls THIS SHOT (not just in the turn)
    if (this.lettersThisShot > 0 && this.turnActive) {
      // Check 7-letter limit
      const store = useGameStore.getState();
      const game = store.game!;
      const currentPlayer = getCurrentPlayer(game);
      const totalLetters = currentPlayer.hand.length + this.collectedLetters.length;
      
      if (totalLetters > this.MAX_HAND_SIZE) {
        console.log(`Over ${this.MAX_HAND_SIZE} letters! Turn ends.`);
        this.endTurn(false);
      } else {
        // Continue shooting
        console.log(`Continue shooting - sunk ${this.lettersThisShot} letter(s) this shot`);
        this.resetCueBall();
        this.syncTurnProgress(true); // full table state snapshot after shot settles
      }
    } else {
      // No balls sunk on this shot - turn ends
      console.log('No balls sunk this shot - turn ends');

      this.endTurn(false);
    }
  }

  private getRemainingRackBallData(): LetterBallData[] {
    const pocketedLetterIds = new Set(this.collectedLetters.map(l => l.id));
    const gutteredLetterIds = new Set(this.gutteredLetters.map(l => l.id));
    return this.letterBallData.filter(data =>
      !pocketedLetterIds.has(data.letter.id) && !gutteredLetterIds.has(data.letter.id)
    );
  }

  private markLastBallGutterIfRackNowEmpty(): void {
    if (this.getRemainingRackBallData().length === 0) {
      this.lastBallClearedByGutterThisShot = true;
    }
  }

  private resetCueBall(): void {
    const cueBall = this.simulation.getCueBall();
    if (cueBall && !this.isShooting) {
      // Already reset, nothing to do
      return;
    }
    
    // Reset to roughly where it stopped (or starting position if scratched)
    if (cueBall) {
      this.simulation.setCueBallPosition({ x: cueBall.x, y: cueBall.y });
    }
  }

  private endTurn(scratched: boolean): void {
    if (!this.turnActive) return;

    // Clear any prior prompt/listener before creating a new turn-end flow.
    this.clearPendingTurnEndUI();
    
    this.turnActive = false;
    
    // Calculate which letters remain on the table (unpocketed and not guttered)
    const pocketedLetterIds = new Set(this.collectedLetters.map(l => l.id));
    const gutteredLetterIds = new Set(this.gutteredLetters.map(l => l.id));
    const remainingRackLetters: RackLetter[] = [];
    
    // Save positions of remaining balls
    for (const data of this.letterBallData) {
      if (!pocketedLetterIds.has(data.letter.id) && !gutteredLetterIds.has(data.letter.id)) {
        const ball = this.simulation.getLetterBall(data.id);
        if (ball) {
          remainingRackLetters.push({
            letter: data.letter,
            x: ball.x,
            y: ball.y,
          });
        }
      }
    }
    
    // Save cue ball position (or reset to null if scratched)
    const cueBall = this.simulation.getCueBall();
    let cueBallPosition = null;
    
    if (!scratched && cueBall) {
      // Save current cue ball position for next turn
      cueBallPosition = { x: cueBall.x, y: cueBall.y };
      console.log(`🎱 Saving cue ball position: (${cueBall.x.toFixed(2)}, ${cueBall.y.toFixed(2)})`);
    } else if (scratched) {
      console.log('🎱 Scratch - cue ball will reset to default position');
    }
    
    // Update game with remaining rack letters at their current positions
    const store = useGameStore.getState();
    const game = store.game!;
    
    if (remainingRackLetters.length > 0) {
      console.log(`🎱 Rack continues: ${remainingRackLetters.length} letters remain at positions:`, 
                  remainingRackLetters.map(rl => `${rl.letter.character}@(${rl.x.toFixed(2)},${rl.y.toFixed(2)})`).join(', '));
    } else {
      console.log('🎱 RACK CLEARED! Next player gets fresh rack.');
      // Reset cue ball to default when rack is cleared
      cueBallPosition = null;
    }
    
    // Return guttered letters to the pool (whether scratched or not) - if rules allow
    let updatedGame = game;
    if (game.rules.returnGutteredLetters && this.gutteredLetters.length > 0) {
      console.log(`♻️ Returning ${this.gutteredLetters.length} guttered letters to pool:`, 
                  this.gutteredLetters.map(l => l.character).join(', '));
      const updatedPool = returnLetters(game.letterPool, this.gutteredLetters);
      updatedGame = { ...game, letterPool: updatedPool };
      this.gutteredThisTurn = this.gutteredLetters.length; // Track for messaging
      this.gutteredLetters = [];
    } else if (!game.rules.returnGutteredLetters && this.gutteredLetters.length > 0) {
      console.log(`🗑️ Guttered letters permanently lost (${this.gutteredLetters.length}):`, 
                  this.gutteredLetters.map(l => l.character).join(', '));
      this.gutteredThisTurn = this.gutteredLetters.length; // Track for messaging
      this.gutteredLetters = [];
    }
    
    // Update currentRackLetters with remaining letters and positions (empty if rack cleared)
    updatedGame = { 
      ...updatedGame, 
      currentRackLetters: remainingRackLetters,
      cueBallPosition 
    };
    console.log('💾 endTurn: Saving', remainingRackLetters.length, 'balls to currentRackLetters');
    store.setGame(updatedGame);

    // Mirror into session state immediately so PoolScene.create() reads the correct
    // position if it runs before the server TURN_HANDOFF arrives.
    const endTurnSessionState = this.multiplayerManager?.getSessionState?.();
    if (endTurnSessionState?.gameState) {
      endTurnSessionState.gameState.cueBallPosition = cueBallPosition;
    }

    // Multiplayer authority:
    // - Scratch: pool turn ends immediately; no board phase.
    // - No new letters collected AND player has no existing letters in hand: pool turn ends here.
    //   (goToBoard will return early and wait for the server's turn_change.)
    // - No new letters but player has letters in hand from prior turns: the player goes to
    //   BoardScene to play them. Do NOT advance the turn here — BoardScene will call endTurn.
    // - New letters collected: turn continues into BoardScene; BoardScene calls endTurn.
    if (this.multiplayerManager && this.isMyTurn) {
      const existingHand = getCurrentPlayer(store.game!).hand;
      const poolTurnEndsHere = scratched || (this.collectedLetters.length === 0 && existingHand.length === 0);
      if (poolTurnEndsHere) {
        // Pool-phase pass: increment consecutive passes and check stalemate.
        const currentPlayer = getCurrentPlayer(store.game!);
        const gameAfterPass = incrementConsecutivePasses(store.game!, currentPlayer.id);
        store.setGame(gameAfterPass);
        const gameCompleted = isStalemate(gameAfterPass) || undefined;

        const wordBoardSnapshot = this.multiplayerManager.getWordBoardSnapshot?.();
        this.multiplayerManager.endTurn({
          currentRackLetters: remainingRackLetters,
          cueBallPosition,
          playerHands: this.buildPlayerHandsSnapshotFromStore(),
          playerScores: this.buildPlayerScoresSnapshotFromStore(),
          letterPool: updatedGame.letterPool,
          consecutivePassesPerPlayer: gameAfterPass.consecutivePassesPerPlayer,
          ...(wordBoardSnapshot ? { wordBoardSnapshot } : {}),
          ...(gameCompleted ? { gameCompleted: true } : {}),
        });
      } else if (this.collectedLetters.length > 0) {
        this.syncTurnProgress(true);
      }
      // else: has existing letters to play → board scene will call endTurn
    }
    
    // Don't add letters to hand here - let goToBoard() handle it
    // Just clear if scratched
    if (scratched) {
      this.collectedLetters = [];
    }
    
    const message = scratched ? 'SCRATCH! Turn ends, no letters collected.' : 
                     this.collectedLetters.length > 0 ? 
                     `Turn complete! Collected: ${this.collectedLetters.map(l => `${l.character.toUpperCase()}(${l.points})`).join(' ')}` :
                     'No letters sunk - turn ends.';
    
    this.turnEndMessageText = this.add.text(640, 300, message, {
      fontSize: '24px',
      color: scratched ? '#ff0000' : '#00ff00',
      backgroundColor: '#000000',
      padding: { x: 16, y: 8 },
      fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    }).setOrigin(0.5).setDepth(100);
    
    // If scratched, skip board and go to next player
    if (scratched) {
      this.time.delayedCall(2000, () => {
        this.clearPendingTurnEndUI();

        // In multiplayer, server turn_change is authoritative.
        // Do not locally advance player order or restart the scene here.
        if (this.isMultiplayerSessionActive()) {
          return;
        }
        
        // Switch to next player and restart pool scene
        const store = useGameStore.getState();
        const game = store.game!;
        console.log('🔴 Scratch: Before switchPlayer, currentRackLetters has', game.currentRackLetters.length, 'balls');
        const currentPlayer = getCurrentPlayer(game);
        
        // Increment consecutive passes (scratch counts as a turn without playing a word)
        const gameWithPass = incrementConsecutivePasses(game, currentPlayer.id);
        const gameWithNextPlayer = switchPlayer(gameWithPass);
        console.log('🔴 Scratch: After switchPlayer, currentRackLetters has', gameWithNextPlayer.currentRackLetters.length, 'balls');
        const backToPoolGame = updatePhase(gameWithNextPlayer, 'pool');
        store.setGame(backToPoolGame);
        this.scene.restart();
      });
      return;
    }

    // No letters collected this turn: do not show board-click prompt.
    // Let goToBoard() handle the no-hand pass flow after the message delay.
    if (this.collectedLetters.length === 0) {
      this.pendingBoardPromptTimer = this.time.delayedCall(2000, () => {
        this.clearPendingTurnEndUI();
        this.goToBoard();
      });
      return;
    }
    
    // Normal turn end - go to board
    this.pendingBoardPromptTimer = this.time.delayedCall(2000, () => {
      this.turnEndContinueText = this.add.text(640, 350, 'Click to play word on board', {
        fontSize: '18px',
        color: '#ffffff',
        backgroundColor: '#2e7d32',
        padding: { x: 10, y: 5 },
        fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }).setOrigin(0.5).setDepth(100);

      this.pendingBoardPointerHandler = () => {
        this.clearPendingTurnEndUI();
        this.goToBoard();
      };
      this.input.once('pointerdown', this.pendingBoardPointerHandler);
    });
  }

  private clearPendingTurnEndUI(): void {
    if (this.pendingBoardPromptTimer) {
      this.pendingBoardPromptTimer.remove(false);
      this.pendingBoardPromptTimer = undefined;
    }

    if (this.pendingBoardPointerHandler) {
      this.input.off('pointerdown', this.pendingBoardPointerHandler);
      this.pendingBoardPointerHandler = undefined;
    }

    if (this.turnEndMessageText) {
      this.turnEndMessageText.destroy();
      this.turnEndMessageText = undefined;
    }

    if (this.turnEndContinueText) {
      this.turnEndContinueText.destroy();
      this.turnEndContinueText = undefined;
    }
  }
  
  private goToBoard(): void {
    console.log('Transitioning to board scene');

    const store = useGameStore.getState();
    let game = store.game!;
    console.log('📋 goToBoard: game.currentRackLetters has', game.currentRackLetters.length, 'balls');

    // When called via the "Play Word" button before endTurn() has run, the store's
    // cueBallPosition is stale (from the start of this turn). Save the live physics
    // position now so BoardScene sends the correct position in the TURN_HANDOFF.
    if (this.turnActive && this.simulation) {
      const cueBall = this.simulation.getCueBall?.();
      const liveCueBallPos = cueBall ? { x: cueBall.x, y: cueBall.y } : null;
      if (liveCueBallPos) {
        game = { ...game, cueBallPosition: liveCueBallPos };
        store.setGame(game);
        // Mirror into session state so PoolScene.create() reads the correct position
        // if it runs before the server TURN_HANDOFF arrives.
        const goBoardSessionState = this.multiplayerManager?.getSessionState?.();
        if (goBoardSessionState?.gameState) {
          goBoardSessionState.gameState.cueBallPosition = liveCueBallPos;
        }
        console.log(`📋 goToBoard: Saving live cue ball position (${liveCueBallPos.x.toFixed(2)}, ${liveCueBallPos.y.toFixed(2)}) before board transition`);
      }
    }

    // Recompute the remaining rack from live state. When called via the "Play Word"
    // button (early exit), endTurn() has not run and the store value is the stale
    // full rack from turn start. Recomputing ensures the handoff payload sent by
    // BoardScene reflects only the balls still on the table.
    const pocketedIds = new Set(this.collectedLetters.map(l => l.id));
    const gutteredIds = new Set(this.gutteredLetters.map(l => l.id));
    const actualRemaining: RackLetter[] = this.letterBallData
      .filter(data => !pocketedIds.has(data.letter.id) && !gutteredIds.has(data.letter.id))
      .flatMap(data => {
        const ball = this.simulation.getLetterBall(data.id);
        return ball ? [{ letter: data.letter, x: ball.x, y: ball.y }] : [];
      });
    if (actualRemaining.length !== game.currentRackLetters.length) {
      console.log(`📋 goToBoard: Correcting currentRackLetters from ${game.currentRackLetters.length} → ${actualRemaining.length} balls`);
      game = { ...game, currentRackLetters: actualRemaining };
      store.setGame(game);
    }

    let currentPlayer = getCurrentPlayer(game);

    // Add collected letters to hand before going to board
    if (this.collectedLetters.length > 0) {
      const updatedPlayer = addLettersToHand(currentPlayer, this.collectedLetters);
      game = updatePlayer(game, currentPlayer.id, updatedPlayer);
      console.log('📋 goToBoard: After updatePlayer, currentRackLetters has', game.currentRackLetters.length, 'balls');
      store.setGame(game);
      
      console.log(`Added ${this.collectedLetters.length} letters to hand before going to board`);
      
      // Clear collected letters so they don't get added again
      this.collectedLetters = [];
      
      // Refresh player reference
      currentPlayer = getCurrentPlayer(game);
    }
    
    // Check if player has any letters in hand
    if (currentPlayer.hand.length === 0) {
      if (this.isMultiplayerSessionActive()) {
        console.log('⚠️ Multiplayer: no letters in hand after pool phase, waiting for authoritative turn_change');
        return;
      }

      console.log('⚠️ Player has no letters in hand - skipping board and switching to next player');
      
      // Build message based on what happened
      let message = '';
      if (this.gutteredThisTurn > 0) {
        const plural = this.gutteredThisTurn > 1 ? 's' : '';
        const verb = this.gutteredThisTurn > 1 ? 'were' : 'was';
        message = `${this.gutteredThisTurn} letter${plural} ${verb} lost to gutter!\nSwitching to next player...`;
      } else {
        message = 'No letters collected!\nSwitching to next player...';
      }
      
      // Show brief message
      const messageText = this.add.text(640, 300, message, {
        fontSize: '24px',
        color: '#FFA500',
        backgroundColor: '#000000',
        padding: { x: 16, y: 8 },
        fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        align: 'center',
      }).setOrigin(0.5).setDepth(100);
      
      // Switch to next player after short delay
      this.time.delayedCall(1500, () => {
        messageText.destroy();
        
        // Increment consecutive passes (no letters collected counts as a turn without playing a word)
        console.log('⚠️ No letters in hand: Before switchPlayer, currentRackLetters has', game.currentRackLetters.length, 'balls');
        const currentPlayer = getCurrentPlayer(game);
        const gameWithPass = incrementConsecutivePasses(game, currentPlayer.id);
        const gameWithNextPlayer = switchPlayer(gameWithPass);
        console.log('⚠️ No letters in hand: After switchPlayer, currentRackLetters has', gameWithNextPlayer.currentRackLetters.length, 'balls');
        const backToPoolGame = updatePhase(gameWithNextPlayer, 'pool');
        store.setGame(backToPoolGame);
        this.scene.restart();
      });
      return;
    }
    
    this.scene.start('BoardScene');
  }

  private updateTurnInfoUI(): void {
    if (!this.turnInfoText) {
      return;
    }

    const collected = this.collectedLetters.map(l => l.character.toUpperCase()).join(' ') || 'none';
    this.turnInfoText.setText(`This turn: ${collected}`);
  }

  private getDefaultCueBallPosition(): { x: number; y: number } {
    return { x: 0, y: TABLE_LENGTH * 0.1 };
  }

  private physicsToScreen(physicsX: number, physicsY: number): { x: number; y: number } {
    // Invert Y: physics Y=0 is at bottom of screen, Y=TABLE_LENGTH is at top
    return {
      x: this.TABLE_OFFSET_X + (physicsX * this.SCALE),
      y: this.TABLE_OFFSET_Y - (physicsY * this.SCALE),
    };
  }

  private screenToPhysics(screenX: number, screenY: number): { x: number; y: number } {
    // Invert Y: screen bottom is physics Y=0, screen top is physics Y=TABLE_LENGTH
    return {
      x: (screenX - this.TABLE_OFFSET_X) / this.SCALE,
      y: (this.TABLE_OFFSET_Y - screenY) / this.SCALE,
    };
  }
}
